import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { formatLanguageCode, getLanguage, LANGUAGES } from '@/constants/languages'
import { ASSIST_PANEL_UI_ENABLED, TRANSLATION_HARD_ENABLED } from '@/constants/feature-flags'
import { clampToMaxLength, REALTIME_CONTEXT_MAX_LENGTH } from '@/constants/meeting'
import {
  SESSION_KIND_EMPHASIS,
  isOfflineSessionKind,
  sanitizeSessionKind,
  sessionKindPanelLayout,
} from '@/constants/session-kind'
import { fmt } from '@/constants/ui-locale'
import { formatElapsedHms } from '@/utils/formatElapsedHms'
import { getLanguageDisplayName } from '@/utils/languageDisplayName'
import { useUiStrings } from '@/hooks/useUiStrings'
import { MicIcon, MicOffIcon, StopIcon, TranslateIcon, XMarkIcon } from '@/components/Icons'
import {
  clearActiveTabAudioCapture,
  getTabAudioDisplayStream,
  hasPreparedTabAudioCapture,
  prepareTabAudioCapture,
} from '@/audio/preparedTabAudioCapture'
import { isCaptureLostError } from '@/audio/CaptureLostError'
import { ArchiveAudioRecorder } from '@/audio/ArchiveAudioRecorder'
import { DisplayMediaSource } from '@/audio/DisplayMediaSource'
import { MediaStreamSource } from '@/audio/MediaStreamSource'
import { useStore } from '@/store/useStore'
import { fetchMeetingById, requestMeetingMinutes, updateMeeting } from '@/services/meetings'
import { fetchRoomEvents } from '@/services/room-events'
import { roomEventsToReplay, withoutTokensAlreadyShown } from '@/utils/roomEventsToReplay'
import { useLiveMemoInsights } from '@/hooks/useLiveMemoInsights'
import { MEDIA_LG_UP, useMediaQuery } from '@/hooks/useMediaQuery'
import { useOnlineStatus } from '@/hooks/useOnlineStatus'
import { usePeriodicAudioPartUpload } from '@/hooks/usePeriodicAudioPartUpload'
import { ROOM_EVENT_FLUSH_INTERVAL_MS, useRoomEventSync } from '@/hooks/useRoomEventSync'
import { useSpaceHotkey } from '@/hooks/useSpaceHotkey'
import { useSharedRoomOffer } from '@/hooks/useSharedRoomOffer'
import { useSharedRoomUi } from '@/hooks/useSharedRoomUi'
import { leaveSharedRoom } from '@/services/meetings'
import { confirmSharedRoomDetection } from '../utils/sharedRoomDetectionHandlers'
import {
  clearSharedRoomRejoinSnapshot,
  loadSharedRoomRejoinSnapshot,
  saveSharedRoomRejoinSnapshot,
} from '../utils/sharedRoomRejoinStorage'
import { resolveMeetingTitleForSave } from '../utils/resolveMeetingTitleForSave'
import {
  buildSharedMeetingPresentation,
  countSharedRoomOthers,
} from '../utils/sharedRoomPresentation'
import {
  sharedRoomProjectManualChangedToast,
  sharedRoomProjectMismatchNotice,
} from '../utils/roomProjectNotice'
import { useLiveInviteRoster } from '@/hooks/useLiveInviteRoster'
import InviteMembersModal from './InviteMembersModal'
import SharedMeetingDetectionModal from './SharedRoom/SharedMeetingDetectionModal'
import SharedRoomHeaderStrip from './SharedRoom/SharedRoomHeaderStrip'
import SharedRoomInRoomControls from './SharedRoom/SharedRoomInRoomControls'
import LiveInviteButton from './SharedRoom/LiveInviteButton'
import SharedRoomLeaveConfirmModal from './SharedRoom/SharedRoomLeaveConfirmModal'
import MeetingTransientToast from './SharedRoom/MeetingTransientToast'
import { useProxySttRecording, type ContextUpdateStatus } from '@/hooks/useProxySttRecording'
import { useRealtimeFeatureToggles } from '@/hooks/useRealtimeFeatureToggles'
import { useAssistModel } from '@/hooks/useAssistModel'
import { useAssistRoutes } from '@/hooks/useAssistRoutes'
import type { RealtimeFeatures } from '@/utils/realtimeFeatures'
import { meetingModeForSessionKind } from '@/utils/meetingMode'
import { useNativeTranscriptSession } from '@/hooks/useNativeTranscriptSession'
import { useScreenCapture } from '@/hooks/useScreenCapture'
import {
  MEETING_CONTEXT,
  type MemoAction,
  type PreviousAnalysis,
  type TranscriptSegment,
} from '@ai-meeting/shared'
import {
  completeMeetingMetadata,
  uploadMeetingAudioInBackground,
} from '../utils/saveCompletedMeetingInBackground'
import { guardPendingUpload } from '@/utils/pendingUploadGuard'
import {
  getMeetingSessionStatusLabel,
  meetingSessionStatusTone,
  resolveNativeSessionState,
  shouldPreserveRecordingSessionStatus,
  shouldRunRecordingTimer,
  type MeetingSessionState,
  type MeetingSessionTone,
} from './meeting-session-status'
import { buildSourceSegments, buildTranslationSegments } from '@/utils/tokensToTranscript'
import {
  buildTranslationSegmentsFromTurns,
  chooseSavedTranslationSegments,
  mergeLiveTranscriptTurns,
  type LiveTranscriptTurn,
} from '@/utils/liveTranscriptTurns'
import { sortBySpeechStart } from '@/utils/speakerTurnOrder'
import { segmentsToTokens } from '@/utils/segmentsToTokens'
import MeetingFeedbackModal from './MeetingFeedbackModal'
import MeetingInsights from './MeetingInsights'
import CopilotFloatingFab from './Copilot/CopilotFloatingFab'
import CopilotFooterButton from './Copilot/CopilotFooterButton'
import {
  MEETING_CONTROL_DIVIDER,
  MEETING_CONTROL_PILL,
  MEETING_CONTROL_PILL_LABEL,
} from './meetingControlPill'
import ActionHintBubble from '@/components/ActionHintBubble'
import { usePanelPresence } from '@/hooks/usePanelPresence'
import { COPILOT_PANEL_TRANSITION_MS } from './Copilot/copilotPanelWidth'
import ContextSavedToast from './ContextSavedToast'
import CopilotSidebar from './Copilot/CopilotSidebar'
import CopilotSetupStrip from './Copilot/CopilotSetupStrip'
import BacklogConnectDialog from '@/components/BacklogConnectDialog'
import { logActivity } from '@/services/activity'
import { openPanelActivities } from './panelActivity'
import { refusalsClearedBy, suggestionIdForQuestion } from './Copilot/retryableRefusals'
import type { BacklogPrerequisites } from './Copilot/retryableRefusals'
import type { ChooseProjectResult } from './Copilot/CopilotSetupStrip'
import AssistModelSelect from './Copilot/AssistModelSelect'
import { parseAssistModel, type AssistModel } from './Copilot/assistModel'
import { assistRoutesToWire, parseAssistRoutes } from './Copilot/assistRoutes'
import CopilotSearchFilter from './Copilot/CopilotSearchFilter'
import { backlogReasonText } from './Copilot/assistBacklogReason'
import {
  countCopilotItems,
  isPendingAnswerCard,
  upsertAssistCard,
  type AssistAskInput,
  type AssistCard,
  type AssistRoute,
} from './Copilot/assist.types'
import { useCopilotPanelUi } from './Copilot/useCopilotPanelUi'
import {
  ChromeHideButton,
  ChromeRestoreStrip,
  CollapsibleChrome,
} from './MeetingMobileChrome'
import { MeetingDurationCheckInModal } from './MeetingDurationCheckInModal'
import {
  advanceDurationWarningAt,
  DURATION_WARNING_FIRST_AT_SECONDS,
  shouldTriggerDurationWarning,
  type DurationWarningStep,
} from '../utils/meetingDurationWarning'
import type { AudioSource, AudioSourceHandlers, RealtimeToken } from '@soniox/client'

const context = {
  general: MEETING_CONTEXT.general,
  terms: MEETING_CONTEXT.terms,
  translation_terms: MEETING_CONTEXT.translationTerms,
}

interface CompletedMeetingSave {
  meeting: {
    id: string
    title: string
    sourceLang: string
    targetLang: string
  }
  durationSeconds: number
  transcript: TranscriptSegment[]
  translationSegments: TranscriptSegment[]
  audio: Blob | null
  // Last known live-memo snapshot (main_content/key_points/actions), captured
  // synchronously at end-meeting time. This needs no async AI round-trip —
  // it's already in memory (or archived) by the time the meeting ends, so
  // it's saved via the synchronous metadata call.
  liveMemo: PreviousAnalysis | null
  /**
   * Copilot cards as this reader saw them, archived half included.
   *
   * Same reasoning as `liveMemo`: already in memory, so it rides the metadata
   * call rather than needing one of its own. A shared room also has these in
   * its event stream, but that stream is replay for the room — this is the
   * meeting's own copy, and the only one an ordinary meeting ever had.
   */
  assistCards: AssistCard[]
}

/**
 * Confirm-items from both snapshots, in archived-then-incoming order, one entry per `content` —
 * and the entry KEEPS THE SPEAKER whichever side supplied it.
 *
 * Plain keep-first dedupe loses that name for good: the archived copy of an item is by definition
 * the older analysis, made when the meeting's roster was still filling up, so it is the copy most
 * likely to have `raised_by: ""`. The later snapshot is what finally names the person, and dropping
 * it as "already seen" would pin the panel to the anonymous version for the rest of the meeting.
 */
function mergeActions(archived: MemoAction[], incoming: MemoAction[]): MemoAction[] {
  const byContent = new Map<string, MemoAction>()
  for (const action of [...archived, ...incoming]) {
    const existing = byContent.get(action.content)
    if (!existing) {
      byContent.set(action.content, action)
      continue
    }
    // Only ever ADD a name; a later snapshot that dropped it must not blank one already shown.
    if (!existing.raised_by?.trim() && action.raised_by?.trim()) {
      byContent.set(action.content, { ...existing, raised_by: action.raised_by })
    }
  }
  return [...byContent.values()]
}

/**
 * Merges the archived pre-restart live-memo snapshot with the freshest live
 * value so a restart's first `summarize.result` (which only reflects the new
 * session's transcript) doesn't blank out prior main_content/key_points/actions.
 * Dedupes exact-match entries so re-analyzed overlapping content doesn't
 * double up.
 */
function mergeArchivedMemo(
  archived: PreviousAnalysis | null,
  incoming: PreviousAnalysis | null
): PreviousAnalysis | null {
  if (!archived) return incoming
  if (!incoming) return archived

  const dedupeStrings = (values: string[]) => [...new Set(values)]
  const dedupeBy = <T,>(items: T[], key: (item: T) => string): T[] => {
    const seen = new Set<string>()
    return items.filter((item) => {
      const k = key(item)
      if (seen.has(k)) return false
      seen.add(k)
      return true
    })
  }

  return {
    main_content: dedupeStrings([...archived.main_content, ...incoming.main_content]),
    key_points: dedupeBy(
      [...archived.key_points, ...incoming.key_points],
      (kp) => `${kp.topic} ${kp.content}`
    ),
    summary: incoming.summary || archived.summary,
    // Deduped on `content` ALONE, on purpose — the speaker lives in `raised_by`, and keying on both
    // would show the same confirm-item twice the moment the server learns who raised it. But the
    // keep-first rule then freezes the FIRST version, and the first version is routinely the one
    // WITHOUT a name: a native meeting opens one socket per participant as they first speak, so the
    // early cycles have a partial roster and attribute nothing. Hence the upgrade below — same item,
    // same position, but a name once either side has one.
    actions: mergeActions(archived.actions, incoming.actions),
  }
}

/** Builds microphone capture constraints while preserving the selected input device. */
function getMicrophoneConstraints(deviceId?: string): MediaTrackConstraints {
  return {
    echoCancellation: true,
    noiseSuppression: true,
    autoGainControl: true,
    ...(deviceId ? { deviceId: { exact: deviceId } } : {}),
  }
}

function waitForStateFlush(): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, 0))
}

// `setTimeout` (not `requestAnimationFrame`) so this still runs when the tab
// is in the background — a meeting's real end usually has the user switching
// away right after clicking "End Meeting" (back to the actual call window),
// and `requestAnimationFrame` callbacks are suspended entirely while a tab is
// backgrounded, which left `prepareSave` (and the audio it archives) stuck
// until the user manually returned to this tab (BRAI-155). Same fix as the
// auto-start `setTimeout` above.
function runInBackground<T>(task: () => Promise<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    window.setTimeout(() => {
      void task().then(resolve, reject)
    }, 0)
  })
}

function normalizeArchivedAudio(audio: Blob | null): Blob | null {
  if (!audio || audio.size === 0) return null
  return audio.type ? audio : new Blob([audio], { type: 'audio/webm' })
}

/**
 * Modal dialog for the `context.update` WebSocket event (BRAI-213 redesign):
 * lets the user tell the next live-summary pass what's currently relevant
 * (e.g. "viewing slide 3"). Applies to Main Content / Key Points / Minutes
 * only — the live caption is never affected. Sends on Save only — no
 * debounced auto-send per keystroke. Save is enabled when the trimmed draft
 * differs from the last saved value (including clearing to empty / whitespace),
 * and disabled while the session isn't ready or a previous save is still
 * pending. Submits always send `value.trim()` and sync the draft to that
 * trimmed string. A successful save keeps the sent value in the textarea
 * (handled by the parent), so reopening the modal shows what's currently in
 * effect. Cancel, the header X, a backdrop click, and Escape all close the
 * modal and discard any unsaved edits by reverting to that last-saved value
 * via the parent's `onClose`.
 */
function RealtimeContextControl({
  value,
  savedValue,
  onValueChange,
  onSubmit,
  onClose,
  disabled,
  status,
  errorMessage,
}: {
  value: string
  savedValue: string
  onValueChange: (value: string) => void
  onSubmit: (context: string) => void
  onClose: () => void
  disabled: boolean
  status: ContextUpdateStatus
  errorMessage: string | null
}) {
  const { t } = useUiStrings()
  const isTooLong = value.length > REALTIME_CONTEXT_MAX_LENGTH
  const trimmedValue = value.trim()
  const canSave =
    !disabled && status !== 'pending' && trimmedValue !== savedValue.trim() && !isTooLong

  useEffect(() => {
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.body.style.overflow = previousOverflow
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [onClose])

  const handleSubmit = () => {
    if (!canSave) return
    if (trimmedValue.length > REALTIME_CONTEXT_MAX_LENGTH) return
    if (trimmedValue !== value) onValueChange(trimmedValue)
    onSubmit(trimmedValue)
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-stretch justify-center p-0 sm:items-center sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="realtime-context-modal-title"
    >
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="animate-scale-in relative flex h-full min-h-0 w-full max-w-full flex-col overflow-hidden rounded-none border-0 border-[var(--border-color)] bg-[var(--bg-secondary)] shadow-xl sm:h-auto sm:max-h-[90vh] sm:max-w-lg sm:rounded-2xl sm:border">
        <div className="flex flex-shrink-0 items-center justify-between border-b border-[var(--border-color)] px-3 py-3 sm:px-6 sm:py-5">
          <div className="flex items-center gap-2">
            <NoteIcon className="h-5 w-5 text-[var(--text-secondary)]" />
            <h2
              id="realtime-context-modal-title"
              className="text-base font-bold text-[var(--text-primary)] sm:text-lg"
            >
              {t.realtimeContextLabel}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t.close}
            className="flex min-h-[44px] min-w-[44px] touch-manipulation items-center justify-center rounded-xl p-2 transition-colors hover:bg-[var(--bg-tertiary)] sm:min-h-0 sm:min-w-0"
          >
            <XMarkIcon className="h-5 w-5 text-[var(--text-tertiary)]" />
          </button>
        </div>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-3 sm:p-6">
          <div className="flex items-start gap-2 rounded-xl border border-blue-100 bg-blue-50 px-3 py-2.5 text-xs leading-relaxed text-blue-800 dark:border-[var(--border-color)] dark:bg-[var(--bg-tertiary)] dark:text-[var(--text-secondary)]">
            <InfoIcon className="mt-0.5 h-4 w-4 shrink-0" />
            <p>
              {t.realtimeContextScopePrefix}
              <strong className="font-semibold">
                {t.liveMainContent}, {t.keyPoints}, {t.meetingMinutes}
              </strong>
              {t.realtimeContextScopeSuffix}
            </p>
          </div>

          <div>
            <label
              htmlFor="realtime-context-input"
              className="mb-1.5 block text-xs font-semibold text-[var(--text-secondary)]"
            >
              {t.realtimeContextEmptyHint}
            </label>
            <textarea
              id="realtime-context-input"
              rows={8}
              value={value}
              onChange={(event) =>
                onValueChange(clampToMaxLength(event.target.value, REALTIME_CONTEXT_MAX_LENGTH))
              }
              disabled={disabled}
              maxLength={REALTIME_CONTEXT_MAX_LENGTH}
              placeholder={t.realtimeContextPlaceholder}
              className="input-field min-h-[180px] w-full resize-y text-xs leading-relaxed sm:text-sm"
            />
            <p className="mt-1 text-right text-[10px] font-semibold text-slate-600 dark:text-[var(--text-tertiary)]">
              {value.length} / {REALTIME_CONTEXT_MAX_LENGTH}
            </p>
            {isTooLong && (
              <p className="mt-1.5 text-xs font-medium text-[var(--error)]" role="alert">
                {fmt(t.realtimeContextTooLong, { max: REALTIME_CONTEXT_MAX_LENGTH })}
              </p>
            )}
            {status === 'error' && (
              <p className="mt-1.5 text-xs font-medium text-[var(--error)]">
                {errorMessage ?? t.realtimeContextUpdateFailed}
              </p>
            )}
          </div>
        </div>

        <div className="flex flex-shrink-0 flex-wrap justify-end gap-2 border-t border-[var(--border-color)] px-3 py-3 pb-[max(1rem,env(safe-area-inset-bottom))] sm:gap-3 sm:px-6 sm:py-4">
          <button
            type="button"
            onClick={onClose}
            className="btn-ghost min-h-[44px] touch-manipulation sm:min-h-0"
          >
            {t.cancel}
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSave}
            className="btn-primary flex min-h-[44px] touch-manipulation items-center justify-center gap-2 disabled:pointer-events-none disabled:opacity-50 sm:min-h-0"
          >
            <SaveIcon className="h-4 w-4" />
            {t.realtimeContextSubmit}
          </button>
        </div>
      </div>
    </div>
  )
}

/** Runs the live meeting recording, translation, memo preview, and completion flow. */
/**
 * Pill colors per status tone. Green reads "audio is flowing", amber "a session exists but audio
 * is not", red "faulted", gray "nothing running" — so the header answers at a glance, before the
 * words are read. Ported from the badge this pill replaced, which colored its dot the same way.
 */
/**
 * How long after this machine's socket goes live to read the room once more.
 *
 * A reader gets a room down two paths — the history it reads on arrival, and
 * its own socket from the moment that is ready — and there is a gap between
 * them. A line said shortly before the socket opened reaches neither: too late
 * for the socket, and still inside the buffer of whoever heard it when the
 * history was read. One flush interval is how long that buffer is held for, and
 * the extra is for the request that carries it. Shorter and the line is still
 * in somebody's buffer; much longer and a reader sits looking at a hole.
 */
const ROOM_HISTORY_GAP_DELAY_MS = ROOM_EVENT_FLUSH_INTERVAL_MS + 2_000

/**
 * How many times to ask the room for its history before giving up.
 *
 * More than once because the read can arrive too early to be allowed. The
 * server checks membership against the `Meeting` row, and on a merge that row
 * is written by the AI service while the browser learns of the merge from a
 * socket frame — two different messengers, no ordering between them. A reader
 * that asks first is told it is not in the room.
 *
 * One attempt used to be the whole story, and a refusal was kept forever:
 * `roomSeedRef` is recorded before the request so that two renders cannot
 * race, which also meant nothing ever asked again. The reader then sat through
 * the meeting with an empty Input pane — and the gap read below never ran
 * either, because the cursor it resumes from is what the failed read produces.
 */
const ROOM_HISTORY_ATTEMPTS = 4
const ROOM_HISTORY_RETRY_MS = 1_000

/**
 * How many times to come back for the rest of a room's history.
 *
 * One was the whole story, and it assumed the room already held everything
 * worth reading. A room that was minted mid-meeting does not: it is created at
 * the merge, and the machine holding the half that came before posts it from
 * memory, a batch at a time. A reader that asks once asks while that is still
 * going on, and whatever had not been posted yet it never saw.
 *
 * Spaced a flush interval apart, so each round asks after the one that could
 * have added something. Six of them cover a backlog of
 * `ROOM_EVENT_BATCH_LIMIT` events a round, and the rounds stop early as soon
 * as the room has twice said it has nothing more.
 */
const ROOM_HISTORY_CATCHUP_ROUNDS = 6

/**
 * How far apart two clocks must be before the lines already captured are moved
 * onto the room's.
 *
 * `timeline_resumed` also comes back on an ordinary reconnect, where the
 * server is handing back the same origin this client already had and the
 * difference is the round trip. Moving a transcript by that is noise, and —
 * worse — it would record a reader's own record as starting a fraction of a
 * second later than it does, which is the measurement the room's history is
 * then cut against.
 */
const MIN_TIMELINE_REBASE_MS = 1_000

const STATUS_PILL_CLASS: Record<MeetingSessionTone, string> = {
  live: 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-300',
  pending:
    'border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300',
  fault:
    'border-red-300 bg-red-50 text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300',
  idle: 'border-slate-200 bg-slate-50 text-slate-600 dark:border-[var(--border-color)] dark:bg-white/5 dark:text-[var(--text-secondary)]',
}

const STATUS_DOT_CLASS: Record<MeetingSessionTone, string> = {
  live: 'bg-emerald-500',
  pending: 'bg-amber-500',
  fault: 'bg-red-500',
  idle: 'bg-slate-400 dark:bg-[var(--text-tertiary)]',
}

/**
 * The earliest moment anything in this list was said, or null when nothing in
 * it says when it was.
 *
 * @param tokens Lines captured by this machine's own socket.
 */
function earliestStartMs(tokens: readonly RealtimeToken[]): number | null {
  let earliest: number | null = null
  for (const token of tokens) {
    if (typeof token.start_ms !== 'number') continue
    if (earliest === null || token.start_ms < earliest) earliest = token.start_ms
  }
  return earliest
}

/**
 * The part of a room's history this reader has no record of.
 *
 * A machine merged into a room mid-meeting is not a late joiner with nothing:
 * it was listening to the same call all along and has its own transcript of
 * it. The room's copy of that same stretch is a second transcription of the
 * same speech — different wording, different sentence breaks, the same
 * meeting — and showing both is how one conversation came to be printed twice.
 *
 * So the room is read for the part that predates this reader's own first line,
 * and that is the whole of what `cutMs` is. A reader with no lines of their
 * own has no cut and keeps all of it, which is every reader of a room they
 * were invited to.
 *
 * @param tokens Lines replayed out of the room's history.
 * @param cutMs  Where this reader's own record starts, on the room's clock.
 */
function roomHistoryThisReaderMissed(
  tokens: readonly RealtimeToken[],
  cutMs: number | null
): RealtimeToken[] {
  if (cutMs === null) return [...tokens]
  // A line the room could not time is kept: an absent `start_ms` is the
  // service declining to say when, and dropping it on a comparison it cannot
  // take part in would lose it for good.
  return tokens.filter((token) => typeof token.start_ms !== 'number' || token.start_ms < cutMs)
}

export default function MeetingChat() {
  const { t, locale } = useUiStrings()
  const user = useStore((s) => s.user)
  const navigate = useNavigate()
  const { isOnline } = useOnlineStatus()
  const wasOnlineRef = useRef(isOnline)
  const settings = useStore((s) => s.settings)
  const setSettings = useStore((s) => s.setSettings)
  const isTeamsPanel = useStore((s) => s.isTeamsPanel)
  const setTargetLang = useStore((s) => s.setTargetLang)
  const clearPendingStartMeeting = useStore((s) => s.clearPendingStartMeeting)
  const deleteMeeting = useStore((s) => s.deleteMeeting)
  const updateMeetingInStore = useStore((s) => s.updateMeeting)
  const addPendingSavedMeeting = useStore((s) => s.addPendingSavedMeeting)
  const removePendingSavedMeeting = useStore((s) => s.removePendingSavedMeeting)
  const showSaveToast = useStore((s) => s.showSaveToast)
  const meetings = useStore((s) => s.meetings)
  const currentMeetingId = useStore((s) => s.currentMeetingId)
  const currentMeeting = meetings.find((meeting) => meeting.id === currentMeetingId)
  /**
   * The grouping key this machine declares to the AI proxy (BRAI-533).
   *
   * Everyone invited into one room runs their own meeting and their own
   * capture; what makes them one room is that they all send this same string
   * instead of their own meeting id. After leaving a room, `soloRoomId` from
   * the server is the code that keeps this machine out of the room it just
   * left (handoff 84). A meeting nobody shared and never left falls back to
   * its id — a room of one, identical to the behaviour before rooms existed.
   */
  const soloRoomId = currentMeeting?.soloRoomId ?? undefined
  const sttRoomId = currentMeeting?.sharedRoomId || soloRoomId || currentMeeting?.id
  // Everyone in the room records the room's history, so it survives whoever
  // closes their tab first (BRAI-539). Declared before the recording hook
  // because it is that hook's `onEvent` tap.
  const recordRoomEvent = useRoomEventSync({
    sharedRoomId: currentMeeting?.sharedRoomId ?? null,
    ownMeetingId: currentMeeting?.id ?? null,
  })
  const [dismissedError, setDismissedError] = useState<string | null>(null)
  const [elapsedSeconds, setElapsedSeconds] = useState(0)
  // Opening layout, chosen by the Mode picked in the create form (BRAI-416).
  // Meeting and Interview start on Input and Output. Nothing is locked: each
  // other panel folds to a strip that opens it again.
  //
  // Read once, as the `useState` seed rather than an effect — a later change to
  // the stored mode must not reach in and reopen panels the user just folded.
  const sessionKind = sanitizeSessionKind(settings.meetingSetup.sessionKind)
  const initialPanelLayout = sessionKindPanelLayout(sessionKind, { inTeamsPanel: isTeamsPanel })
  const emphasis = SESSION_KIND_EMPHASIS[sessionKind]
  const featureSendRef = useRef<(features: Partial<RealtimeFeatures>) => boolean>(() => false)
  const assistModelSendRef = useRef<(model: AssistModel) => boolean>(() => false)
  const assistRoutesSendRef = useRef<(routes: AssistRoute[]) => boolean>(() => false)
  const assistModel = useAssistModel({
    stored: parseAssistModel(settings.assistModel) ?? undefined,
    onRemember: (model) => setSettings({ assistModel: model }),
    sendUpdate: (model) => assistModelSendRef.current(model),
  })
  /**
   * Search routes are room-wide in a shared room. The recorder owns the
   * Copilot flow and applies the same selection that an ordinary meeting
   * would; listeners follow that selection and cannot overwrite it.
   */
  const assistRoutes = useAssistRoutes({
    stored: parseAssistRoutes(settings.assistRoutes) ?? undefined,
    onRemember: (routes) => setSettings({ assistRoutes: assistRoutesToWire(routes) }),
    sendUpdate: (routes) => assistRoutesSendRef.current(assistRoutesToWire(routes)),
  })
  /** Why the Backlog button is dimmed, in the reader's language. */
  const backlogRouteReasonText = backlogReasonText(assistRoutes.backlogReason, t)
  /**
   * Whether the panel still offers to connect Backlog.
   *
   * Not while the route is off: the reader has said they do not want ticket
   * answers, and an invitation to set up the thing they just declined is the
   * panel arguing with them. The project picker stays — a typed question about
   * a project is still answered, and the minutes use it either way.
   */
  const offersBacklogSetup = assistRoutes.routes.has('backlog')
  const realtimeFeatures = useRealtimeFeatureToggles({
    meetingId: sttRoomId,
    initialMemoOpen: initialPanelLayout.memoOpen,
    initialKeyPointsOpen: initialPanelLayout.keyPointsOpen,
    initialBatchOpen: initialPanelLayout.batchOpen,
    sendFeaturesUpdate: (features) => featureSendRef.current(features),
    onUpdateError: (code) => {
      showSaveToast({
        kind: 'error',
        message: code === 'not_supported' ? t.featuresAssistUnavailable : t.featuresUpdateError,
      })
    },
  })
  const {
    memoOpen,
    keyPointsOpen,
    batchOpen,
    assistOn,
    toggleMemo,
    toggleKeyPoints,
    toggleBatch,
    setAssist,
    getFeatures,
    handleReady,
    handleUpdated,
    handleError,
  } = realtimeFeatures
  const [sourceOpen, setSourceOpen] = useState(initialPanelLayout.sourceOpen)
  const [targetOpen, setTargetOpen] = useState(initialPanelLayout.targetOpen)
  // Mobile (< lg) footer collapse — desktop always shows the control bar.
  const isLgUp = useMediaQuery(MEDIA_LG_UP)
  const [mobileFooterOpen, setMobileFooterOpen] = useState(true)
  const [isCopilotOpen, setIsCopilotOpen] = useState(false)
  /**
   * Whether the Copilot hint has already done its job this session.
   *
   * Session-only and deliberately not stored: it is here to stop the bubble
   * reappearing every time the panel is closed, not to remember anybody.
   */
  const [copilotHintSpent, setCopilotHintSpent] = useState(false)
  const copilotUi = useCopilotPanelUi()
  const { markCopilotOpened, bumpUnread } = copilotUi
  const assistCardCountRef = useRef(0)
  const handleToggleCopilot = useCallback(() => {
    setIsCopilotOpen((open) => {
      if (!open) markCopilotOpened()
      // Counted apart rather than as one "toggled": a panel opened often and
      // closed at once says something a single number cannot (BRAI-677).
      logActivity(open ? 'copilot.closed' : 'copilot.opened')
      return !open
    })
    setCopilotHintSpent(true)
    setAssist(!isCopilotOpen)
  }, [isCopilotOpen, markCopilotOpened, setAssist])
  const [durationWarningStep, setDurationWarningStep] = useState<DurationWarningStep>('closed')
  const [nextWarningAtSeconds, setNextWarningAtSeconds] = useState(
    DURATION_WARNING_FIRST_AT_SECONDS
  )
  const durationWarningActionLockRef = useRef(false)
  const [feedbackOpen, setFeedbackOpen] = useState(false)
  const [completedDurationSeconds, setCompletedDurationSeconds] = useState<number | null>(null)
  const [isSavingMeeting, setIsSavingMeeting] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [isMicMuted, setIsMicMuted] = useState(!settings.meetingSetup.hasMicrophone)
  const [isStartingMeeting, setIsStartingMeeting] = useState(false)
  const [isEndingMeeting, setIsEndingMeeting] = useState(false)
  const [startError, setStartError] = useState<string | null>(null)
  const [isChangingTargetLang, setIsChangingTargetLang] = useState(false)
  const revertTargetLangRef = useRef<string | null>(null)
  const [showOfflineEndPrompt, setShowOfflineEndPrompt] = useState(false)
  const [isContextPanelOpen, setIsContextPanelOpen] = useState(false)
  const translationHardButtonRef = useRef<HTMLButtonElement>(null)
  const [contextInputValue, setContextInputValue] = useState('')
  // Last value the server confirmed via `context.updated`. Reopening the
  // modal restores this (rather than a blank textarea) so the user can see
  // and continue editing what's currently in effect; discarding an in-progress
  // edit (Cancel/X/Escape/backdrop) also reverts to this value.
  const [savedContextValue, setSavedContextValue] = useState('')
  const contextInputValueRef = useRef(contextInputValue)
  contextInputValueRef.current = contextInputValue
  // Mirrors `savedContextValue` so `persistMeetingAndNavigate` (a stable
  // `useCallback`) can read the latest saved context at finalize time
  // without needing it in its dependency list.
  const savedContextValueRef = useRef(savedContextValue)
  savedContextValueRef.current = savedContextValue
  const contextButtonRef = useRef<HTMLDivElement>(null)
  const [justUpdatedContext, setJustUpdatedContext] = useState(false)
  const [contextSavedCharCount, setContextSavedCharCount] = useState<number | null>(null)
  const [archivedTokens, setArchivedTokens] = useState<RealtimeToken[]>([])
  const archivedTokensRef = useRef<RealtimeToken[]>([])
  /** Appends finalized tokens when a recording restart would otherwise clear hook state. */
  /** Meetings whose stored transcript has already been folded into the archive. */
  const seededArchiveMeetingIdsRef = useRef<Set<string>>(new Set())

  const appendArchivedTokens = useCallback((tokens: RealtimeToken[]) => {
    if (tokens.length === 0) return
    const next = [...archivedTokensRef.current, ...tokens]
    archivedTokensRef.current = next
    setArchivedTokens(next)
  }, [])
  const [archivedLiveTurns, setArchivedLiveTurns] = useState<LiveTranscriptTurn[]>([])
  /**
   * Preserves source-aligned timeline cards when restarting the recording socket.
   *
   * @param turns Latest cards that the hook will clear during restart.
   */
  /**
   * Puts a meeting's stored transcript back into the archive before a native
   * session starts.
   *
   * The bot leaves the instant the Teams panel goes away — `pagehide` stops the
   * session outright — so a user who clicks Leave and then Cancel lands on a
   * brand-new session whose socket only replays its own segments. The previous
   * session's transcript is already on the meeting by then (the gateway projects
   * it when the bot's departure is reconciled), so this is where it comes back
   * from (BRAI-447).
   *
   * Once per meeting: `appendArchivedTokens` appends, so a second Start on the
   * same meeting would otherwise show every earlier line twice.
   *
   * @param meetingId Meeting about to get a native session.
   * @param stored Transcript already saved against that meeting.
   */
  const seedArchiveFromStoredTranscript = useCallback(
    async (meetingId: string) => {
      // Marked before the await, so pressing Start twice cannot race two fetches
      // and append the same lines twice.
      if (seededArchiveMeetingIdsRef.current.has(meetingId)) return
      seededArchiveMeetingIdsRef.current.add(meetingId)
      try {
        const stored = await fetchMeetingById(meetingId)
        const segments = stored.transcript ?? []
        if (segments.length > 0) appendArchivedTokens(segmentsToTokens(segments))
      } catch {
        // Best effort. Restoring earlier lines must never stop the meeting from
        // starting — an empty Input pane is worse than a late one, but a meeting
        // that refuses to start is worse than both.
      }
    },
    [appendArchivedTokens]
  )

  const appendArchivedLiveTurns = useCallback((turns: LiveTranscriptTurn[]) => {
    if (turns.length === 0) return
    setArchivedLiveTurns((current) => mergeLiveTranscriptTurns(current, turns))
  }, [])
  const [archivedMemo, setArchivedMemo] = useState<PreviousAnalysis | null>(null)
  const archivedMemoRef = useRef<PreviousAnalysis | null>(null)
  /** Preserves the last live-memo snapshot when a recording restart would otherwise clear it (setMemo(null) in useProxySttRecording.start()). */
  const captureArchivedMemo = useCallback((memo: PreviousAnalysis | null) => {
    if (!memo) return
    archivedMemoRef.current = memo
    setArchivedMemo(memo)
  }, [])

  const [archivedAssistCards, setArchivedAssistCards] = useState<AssistCard[]>([])
  /**
   * Keeps the Copilot cards a room produced before this reader arrived (BRAI-539).
   *
   * Held here rather than pushed into the recording hook, which owns only what its own socket
   * heard: the hook clears its cards on every start, and seeding it would make a restart look
   * like the room saying all of this again.
   */
  const captureArchivedAssistCards = useCallback((cards: AssistCard[]) => {
    if (cards.length === 0) return
    setArchivedAssistCards((current) => cards.reduce(upsertAssistCard, current))
  }, [])

  /**
   * The first read of a room, kept so the second one can wait for it.
   *
   * Holds the promise rather than just a flag: the second read resumes from the
   * cursor this one produces, so a slow first read has to delay it rather than
   * cancel it. Doubles as the once-per-room guard — it is assigned before the
   * first `await`, so StrictMode's second mount finds it.
   */
  const roomSeedRef = useRef<{ roomId: string; done: Promise<void> } | null>(null)
  /** Where the last read of this room got to, for the next read to resume from. */
  const roomHistoryCursorRef = useRef<{ roomId: string; cursor: string } | null>(null)
  /**
   * Where this reader's own record starts, on the room's clock.
   *
   * Null for a reader who has none — anyone who opened a room they were
   * invited to, which is every reader this existed for before auto-join. Set
   * only when a running meeting is merged into somebody else's room, and read
   * by {@link roomHistoryThisReaderMissed}.
   */
  const roomHistoryCutMsRef = useRef<number | null>(null)
  const closedRoomGapIdsRef = useRef(new Set<string>())
  /**
   * Put what the room already said on screen, for somebody who joined late
   * (BRAI-539).
   *
   * The proxy broadcasts a room's transcript but never replays it, so a socket
   * opened halfway through starts blank and the first half is gone. These rows
   * are the only copy of it.
   *
   * Once per room: `appendArchivedTokens` appends, so running twice would show
   * every earlier line twice. Best effort for the same reason the native seed
   * beside it is — an empty Input pane is worse than a late one, and a meeting
   * that refuses to start is worse than both.
   */
  const seedArchiveFromRoomHistory = useCallback(
    (sharedRoomId: string) => {
      const started = roomSeedRef.current
      if (started?.roomId === sharedRoomId) return started.done
      const done = (async () => {
        for (let attempt = 1; attempt <= ROOM_HISTORY_ATTEMPTS; attempt += 1) {
          try {
            const { events, cursor } = await fetchRoomEvents(sharedRoomId)
            roomHistoryCursorRef.current = { roomId: sharedRoomId, cursor }
            const replay = roomEventsToReplay(events)
            if (replay.tokens.length > 0) appendArchivedTokens(replay.tokens)
            if (replay.memo) captureArchivedMemo(replay.memo)
            captureArchivedAssistCards(replay.assistCards)
            return
          } catch {
            if (attempt === ROOM_HISTORY_ATTEMPTS) {
              // Said out loud at last. It used to fail in complete silence,
              // which is why a reader could sit through a whole meeting with
              // an empty Input pane and nothing anywhere to say why.
              console.warn(
                `Could not read the history of room ${sharedRoomId} after ${attempt} attempts`
              )
              return
            }
            await new Promise((resolve) => setTimeout(resolve, ROOM_HISTORY_RETRY_MS))
          }
        }
      })()
      // Recorded before anything awaits, so two renders cannot race two fetches.
      roomSeedRef.current = { roomId: sharedRoomId, done }
      return done
    },
    [appendArchivedTokens, captureArchivedMemo, captureArchivedAssistCards]
  )

  /**
   * Keep reading the room until it has nothing left this reader has not seen.
   *
   * Two different holes, and the same request closes both.
   *
   * The first is the gap between a reader's two paths into a room: the history
   * is read when the page opens, the socket starts delivering when it is ready
   * some seconds later, and a line said in between reaches neither — the
   * machine that heard it was still holding it in a buffer when this reader
   * read the room, and it had stopped being new by the time this socket
   * opened.
   *
   * The second is a room that did not exist yet. Auto-join mints one in the
   * middle of a meeting, and the half that came before it is in the memory of
   * the machine that was recording, which posts it a batch at a time once the
   * room is there to post it to. A reader that asks once asks while that is
   * still arriving.
   *
   * So it asks repeatedly, from the cursor each reply returns, and stops when
   * two rounds running bring back nothing. What comes back is filtered twice:
   * against the stretch this reader already has a record of
   * ({@link roomHistoryThisReaderMissed}), and against the lines already on
   * screen, since the window these rounds cover overlaps the socket.
   */
  const closeRoomHistoryGap = useCallback(
    async (sharedRoomId: string) => {
      // The read on arrival may still be in flight, and it is what produces the
      // cursor this resumes from. Waiting for it makes a slow first read delay
      // this one rather than being skipped past.
      await roomSeedRef.current?.done
      let emptyRounds = 0
      for (let round = 1; round <= ROOM_HISTORY_CATCHUP_ROUNDS; round += 1) {
        if (round > 1) {
          await new Promise((resolve) => setTimeout(resolve, ROOM_EVENT_FLUSH_INTERVAL_MS))
        }
        const resumeFrom = roomHistoryCursorRef.current
        // No cursor is not a reason to give up, which it used to be. It means
        // the read on arrival was refused — a merge writes the row that proves
        // membership through the AI service, so a reader can be asking before
        // the row is there — or never ran at all, which is now the ordinary
        // case for a merge. Reading from the beginning costs the room one
        // larger reply and is the only thing that recovers either.
        const after = resumeFrom?.roomId === sharedRoomId ? resumeFrom.cursor : undefined
        try {
          const { events, cursor } = await fetchRoomEvents(sharedRoomId, after)
          roomHistoryCursorRef.current = { roomId: sharedRoomId, cursor }
          if (events.length === 0) {
            // Twice, because one empty round proves nothing: the machine
            // holding the room's first half posts it in batches, and between
            // two of them the room genuinely has nothing new.
            emptyRounds += 1
            if (emptyRounds === 2) return
            continue
          }
          emptyRounds = 0
          const replay = roomEventsToReplay(events)
          const missing = withoutTokensAlreadyShown(
            roomHistoryThisReaderMissed(replay.tokens, roomHistoryCutMsRef.current),
            [...archivedTokensRef.current, ...recordingFinalTokensRef.current]
          )
          // Appended to the archive rather than merged into the live stream: the
          // archive renders above it, which is where a line said before this
          // socket opened belongs.
          if (missing.length > 0) appendArchivedTokens(missing)
          if (replay.memo) captureArchivedMemo(replay.memo)
          captureArchivedAssistCards(replay.assistCards)
        } catch {
          // Same bargain as the read on arrival: a reader missing one line is
          // better off than a reader whose meeting stopped to say so. The next
          // round asks again from the same place.
        }
      }
    },
    [appendArchivedTokens, captureArchivedMemo, captureArchivedAssistCards]
  )

  // On arrival rather than on Start: a late joiner should be able to read what
  // they missed before deciding to record anything, and a mid-meeting reload
  // lands here too.
  //
  // Not once this machine has a timeline of its own, though. That is a merge —
  // the room arrived on a meeting that was already running — and which part of
  // the room's history this reader actually missed is not known until `ready`
  // hands over the room's clock, which has not happened yet. Reading now would
  // put the room's account of this meeting's middle above this reader's own
  // account of the same minutes. `closeRoomHistoryGap` reads it instead, from
  // the beginning, after the clocks agree.
  const sharedRoomId = currentMeeting?.sharedRoomId ?? null
  useEffect(() => {
    if (!sharedRoomId) return
    if (meetingStartedAtRef.current !== null) return
    void seedArchiveFromRoomHistory(sharedRoomId)
  }, [sharedRoomId, seedArchiveFromRoomHistory])
  const autoStartAttemptRef = useRef<{ meetingId: string; pendingId: string } | null>(null)
  const archiveRecorderRef = useRef(new ArchiveAudioRecorder())
  const preparedSaveRef = useRef<CompletedMeetingSave | null>(null)
  const preparedSavePromiseRef = useRef<Promise<CompletedMeetingSave> | null>(null)
  const deferredPrepareSaveRef = useRef<(() => Promise<CompletedMeetingSave>) | null>(null)
  const endMeetingRequestedRef = useRef(false)
  const endingMeetingTitleRef = useRef<string | null>(null)
  useEffect(() => {
    endingMeetingTitleRef.current = null
  }, [currentMeeting?.id])
  const handleEndMeetingRef = useRef<() => void>(() => undefined)
  const recordingFinalTokensRef = useRef<RealtimeToken[]>([])
  const nativeFinalTokensRef = useRef<RealtimeToken[]>([])
  const elapsedSecondsRef = useRef(0)
  const meetingStartedAtRef = useRef<number | null>(null)
  const micStreamRef = useRef<MediaStream | null>(null)

  const audioSourceRef = useRef(settings.audioSource)
  audioSourceRef.current = settings.audioSource
  const microphoneDeviceIdRef = useRef(settings.meetingSetup.microphoneDeviceId)
  microphoneDeviceIdRef.current = settings.meetingSetup.microphoneDeviceId
  const hasMicrophoneRef = useRef(settings.meetingSetup.hasMicrophone)
  hasMicrophoneRef.current = settings.meetingSetup.hasMicrophone

  const innerSourceRef = useRef<DisplayMediaSource | MediaStreamSource | null>(null)
  // Read by the capture factory below, which rebuilds handlers and sources on
  // every (re)start and so cannot close over the state itself.
  const isMicMutedRef = useRef(isMicMuted)
  isMicMutedRef.current = isMicMuted
  const targetLangRef = useRef(settings.targetLang)
  targetLangRef.current = settings.targetLang
  const contextRef = useRef(settings.context)
  contextRef.current = settings.context

  /**
   * Surfaces a toast and dismisses the "offline cannot end" prompt the moment
   * the browser reports connectivity is back. Tracks the previous flag so a
   * fresh mount while online doesn't trigger a spurious "restored" toast.
   */
  useEffect(() => {
    if (isOnline) {
      if (!wasOnlineRef.current) {
        showSaveToast({ kind: 'success', message: t.offlineRestoredToast })
        setShowOfflineEndPrompt(false)
      }
    }
    wasOnlineRef.current = isOnline
  }, [isOnline, showSaveToast, t.offlineRestoredToast])

  // Crossing up to desktop must always restore the footer; collapse is mobile-only.
  useEffect(() => {
    if (isLgUp) {
      setMobileFooterOpen(true)
    }
  }, [isLgUp])

  /** Applies mute state to both direct microphone and mixed tab-audio capture. */
  const applyMicMuted = useCallback((muted: boolean) => {
    if (innerSourceRef.current instanceof DisplayMediaSource) {
      innerSourceRef.current.setMicrophoneEnabled(!muted)
      return
    }

    if (innerSourceRef.current instanceof MediaStreamSource) {
      innerSourceRef.current.setMicrophoneEnabled(!muted)
      return
    }

    micStreamRef.current?.getAudioTracks().forEach((track) => {
      track.enabled = !muted
    })
  }, [])

  const proxySource = useMemo<AudioSource>(
    () => ({
      start: async (handlers: AudioSourceHandlers) => {
        if (audioSourceRef.current === 'system') {
          const source = new DisplayMediaSource({
            microphoneDeviceId: microphoneDeviceIdRef.current,
            hasMicrophone: hasMicrophoneRef.current,
            onMixedStream: (stream) => {
              if (!archiveRecorderRef.current.isRecording()) {
                archiveRecorderRef.current.start(stream)
              }
            },
          })
          innerSourceRef.current = source
          await source.start(handlers)
          // A reconnect builds a brand-new capture, and a new capture starts
          // unmuted. Carry the switch across so an outage during a mute does
          // not hand the meeting back a live microphone (BRAI-403).
          source.setMicrophoneEnabled(!isMicMutedRef.current)
          return
        }

        const stream =
          micStreamRef.current &&
          micStreamRef.current.getAudioTracks().some((track) => track.readyState === 'live')
            ? micStreamRef.current
            : await navigator.mediaDevices.getUserMedia({
                audio: getMicrophoneConstraints(microphoneDeviceIdRef.current),
                video: false,
              })
        micStreamRef.current = stream
        if (!archiveRecorderRef.current.isRecording()) {
          archiveRecorderRef.current.start(stream.clone())
        }

        const source = new MediaStreamSource(stream)
        innerSourceRef.current = source
        await source.start({
          ...handlers,
          // Muting the track only turns the capture into digital silence, and
          // the proxy answers silence with invented transcript — the trap pause
          // fell into (BRAI-209), reported again against mute as BRAI-403. So
          // drop the chunks outright, the way `pause` does inside
          // `useProxySttRecording`.
          //
          // Deliberately only on this branch: the system-audio branch above
          // shares a tab, which has to keep streaming while the microphone is
          // muted. Wrapping the handlers per branch makes that structural
          // rather than a condition someone has to remember.
          onData: (chunk) => {
            if (isMicMutedRef.current) return
            handlers.onData(chunk)
          },
        })
        source.setMicrophoneEnabled(!isMicMutedRef.current)
      },
      stop: () => {
        if (innerSourceRef.current instanceof DisplayMediaSource) {
          innerSourceRef.current.stop()
        } else {
          innerSourceRef.current?.stop()
        }
        innerSourceRef.current = null

        micStreamRef.current?.getTracks().forEach((track) => track.stop())
        micStreamRef.current = null
      },
      pause: () => innerSourceRef.current?.pause?.(),
      resume: () => innerSourceRef.current?.resume?.(),
    }),
    []
  )

  // Both capture paths keep one meeting timeline with the server. The header clock is reported when
  // a socket opens, so the server anchors the meeting where this clock already is; every event then
  // arrives on the meeting's clock and nothing on this side shifts it. Read per socket, not
  // captured: in native mode each participant connects when they first speak. 0 before Start means
  // "no hint", which is what the server already assumed.
  const getMeetingElapsedMs = () => {
    if (meetingStartedAtRef.current === null) return 0
    // Nothing from a machine sitting in somebody else's room. The field is a
    // hint about where this client's timeline starts, and a room has one
    // origin — the machine it is named after. Offering ours invites the
    // server to take it, and then `ready` comes back `timeline_resumed:
    // false`, `handleTimelineAnchor` declines the room's seed, and every
    // replayed line is stamped against a zero that is not the room's.
    //
    // The server-minted solo room counts as ours (handoff 84 / BRAI-791). Before
    // it existed, a `sttRoomId` that was not this meeting's id meant one thing
    // only — somebody else's room — and this guard was written to that. A
    // meeting carrying on alone under a code that is not its own id is the
    // third case: the only timeline in there is this one, so withholding the
    // origin anchors the fresh session at zero, and the clock beside the
    // transcript restarts the moment the machine the room was named after
    // walks out of it.
    if (sttRoomId !== currentMeeting?.id && sttRoomId !== soloRoomId) return 0
    return Date.now() - meetingStartedAtRef.current
  }
  // Only when the server says it re-joined a timeline it already had - a reconnect, or a shared room
  // joined late: its record predates this client's, so its origin wins. On a fresh timeline the
  // server just took the origin from us, and moving ours would chase our own tail.
  const handleTimelineAnchor = ({ seedMs, resumed }: { seedMs: number; resumed: boolean }) => {
    if (!resumed) return
    const now = Date.now()
    const ownStartedAt = meetingStartedAtRef.current
    meetingStartedAtRef.current = now - seedMs
    // The rest is only for a meeting that was recording on its own clock and
    // has just been merged into somebody else's room. A reader who had not
    // started has nothing stamped to move, and the machine the room is named
    // after is the origin — its clock is the one everyone else just adopted.
    if (ownStartedAt === null || sttRoomId === currentMeeting?.id) return
    const shiftMs = seedMs - (now - ownStartedAt)
    if (shiftMs < MIN_TIMELINE_REBASE_MS) return
    // Read before the shift is applied, and kept: this is where this reader's
    // own account of the meeting begins on the room's clock, and therefore
    // where the room's own account stops being something they missed.
    const ownFirstLineMs = earliestStartMs(recordingFinalTokensRef.current)
    if (ownFirstLineMs !== null) roomHistoryCutMsRef.current = ownFirstLineMs + shiftMs
    // Without this the pane carries two clocks — everything from before the
    // merge against this machine's own origin, everything after against the
    // room's — and the time printed beside the transcript jumps forward in the
    // middle of a sentence.
    recordingRef.current?.rebaseTranscriptClock(shiftMs)
  }
  /**
   * Stops asking for the rest of the session after the reader leaves a room.
   *
   * Session-scoped on purpose, and separate from the stored preference: "I am
   * done with this meeting" is not "never ask me again", and a reader who
   * leaves one room should still be offered the next meeting's.
   */
  const [leftSharedRoom, setLeftSharedRoom] = useState(false)
  /** Sync guard for handlers that run before React re-renders after Leave/rejoin. */
  const leftSharedRoomRef = useRef(false)
  const [leavingSharedRoom, setLeavingSharedRoom] = useState(false)
  const liveMeetingScopeRef = useRef<string | null>(null)
  const [inviteMembersOpen, setInviteMembersOpen] = useState(false)
  /**
   * Breaks the circle between the two hooks: the offer needs the socket to
   * answer on, and the socket needs the offer to hand its frames to. The ref
   * is filled on the line after the recording hook runs, which is before any
   * frame can arrive.
   */
  const recordingRef = useRef<ReturnType<typeof useProxySttRecording> | null>(null)

  /**
   * Records the room on the reader's own meeting, so a reload stays in it.
   *
   * `onlyWhenUnset` comes from which frame announced the merge: the machine
   * that stays keeps the room its meeting already names, because overwriting
   * is how a meeting merged into one room is quietly moved into another.
   */
  const handleJoinedSharedRoom = useCallback(
    (roomId: string, { onlyWhenUnset }: { onlyWhenUnset: boolean }) => {
      if (leftSharedRoomRef.current || leavingSharedRoom) return
      const ownId = currentMeeting?.id
      if (!ownId) return
      if (onlyWhenUnset && currentMeeting?.sharedRoomId) return
      updateMeetingInStore(ownId, { sharedRoomId: roomId })
    },
    [
      currentMeeting?.id,
      currentMeeting?.sharedRoomId,
      leavingSharedRoom,
      updateMeetingInStore,
    ]
  )

  const joinToastSentRef = useRef(false)

  const sharedRoomMeetingId = currentMeeting?.id ?? null
  const sharedRoom = useSharedRoomOffer({
    sendReply: (matchId, accept) =>
      recordingRef.current?.sendSharedRoomReply(matchId, accept) ?? false,
    sendRejoin: (roomId) => recordingRef.current?.sendSharedRoomRejoin(roomId) ?? false,
    rejoin: (roomId) => recordingRef.current?.rejoinSharedRoom(roomId) ?? false,
    onJoined: handleJoinedSharedRoom,
    onPersistedRoomRestore: () => {
      joinToastSentRef.current = true
    },
    currentRoomId: currentMeeting?.sharedRoomId ?? null,
    initialDismissedMatch: sharedRoomMeetingId
      ? loadSharedRoomRejoinSnapshot(sharedRoomMeetingId)
      : null,
    onDismissedMatchChange: (snapshot) => {
      if (!sharedRoomMeetingId) return
      if (snapshot) saveSharedRoomRejoinSnapshot(sharedRoomMeetingId, snapshot)
      else clearSharedRoomRejoinSnapshot(sharedRoomMeetingId)
    },
    onRejoinAbandoned: () => {
      leftSharedRoomRef.current = true
      setLeftSharedRoom(true)
    },
  })

  useEffect(() => {
    const id = sharedRoomMeetingId
    if (id === liveMeetingScopeRef.current) return
    liveMeetingScopeRef.current = id
    leftSharedRoomRef.current = false
    setLeftSharedRoom(false)
    setLeavingSharedRoom(false)
    if (!id) return
    sharedRoom.resetForLiveMeeting(
      currentMeeting?.sharedRoomId ? null : loadSharedRoomRejoinSnapshot(id)
    )
  }, [sharedRoomMeetingId, currentMeeting?.sharedRoomId, sharedRoom.resetForLiveMeeting])

  const inSharedRoom =
    !leftSharedRoom &&
    (Boolean(currentMeeting?.sharedRoomId) || sharedRoom.state.kind === 'joined')
  const sharedRoomRoomId =
    currentMeeting?.sharedRoomId ??
    (sharedRoom.state.kind === 'joined' ? sharedRoom.state.roomId : null)

  const sharedRoomUi = useSharedRoomUi({
    offerState: sharedRoom.state,
    lastDismissedMatch: sharedRoom.lastDismissedMatch,
    leftSharedRoom,
    inSharedRoom,
  })

  useEffect(() => {
    if (currentMeeting?.sharedRoomId && leftSharedRoom) {
      setLeftSharedRoom(false)
    }
  }, [currentMeeting?.sharedRoomId, leftSharedRoom])

  const sharedRoomPresentation = useMemo(() => {
    if (!inSharedRoom || !sharedRoomRoomId || !currentMeeting) return null
    const liveOffer =
      sharedRoom.state.kind === 'joined' ||
      sharedRoom.state.kind === 'waiting' ||
      sharedRoom.state.kind === 'offered' ||
      sharedRoom.state.kind === 'requested'
        ? sharedRoom.state
        : null
    const meta = liveOffer?.meta ?? sharedRoom.lastDismissedMatch?.meta ?? {}
    const peerNames = liveOffer?.peerNames ?? sharedRoom.lastDismissedMatch?.peerNames ?? []
    /**
     * On the machine the room is named after, the room's name is its own.
     *
     * There `peerMeetingTitle` is the OTHER meeting's label — often the
     * `Meeting#N` its owner never changed — so adopting it would replace the
     * chosen room name with a default on the one machine that already has it.
     */
    const roomIsOwnMeeting = sharedRoomRoomId === currentMeeting.id
    const details = { peerNames, ...meta }
    if (roomIsOwnMeeting) delete details.peerMeetingTitle
    const presentation = buildSharedMeetingPresentation({
      details,
      roomId: sharedRoomRoomId,
      ownMeetingTitle: currentMeeting.title,
      ownUserName: user?.displayName ?? undefined,
      meetingCreatedAt: currentMeeting.createdAt,
      meetingSourceLang: currentMeeting.sourceLang,
      meetingTargetLang: currentMeeting.targetLang,
      t,
      locale,
    })
    return presentation
  }, [
    currentMeeting,
    inSharedRoom,
    locale,
    sharedRoom.lastDismissedMatch,
    sharedRoom.state,
    sharedRoomRoomId,
    t,
    user,
  ])

  const liveInviteActiveMembers = sharedRoomPresentation?.participants ?? []
  const { waitingMembers: liveInviteWaitingMembers, reload: reloadLiveInviteRoster } =
    useLiveInviteRoster(currentMeetingId, liveInviteActiveMembers)

  const sharedRoomProject = useMemo(() => {
    if (!inSharedRoom) return undefined
    const liveOffer =
      sharedRoom.state.kind === 'joined' ||
      sharedRoom.state.kind === 'waiting' ||
      sharedRoom.state.kind === 'offered' ||
      sharedRoom.state.kind === 'requested'
        ? sharedRoom.state
        : null
    const meta = liveOffer?.meta ?? sharedRoom.lastDismissedMatch?.meta ?? {}
    return meta.roomProject
  }, [inSharedRoom, sharedRoom.lastDismissedMatch, sharedRoom.state])

  const roomProjectKey = sharedRoomProject?.key.trim() ?? null

  const roomProjectMismatchNoticeText = useMemo(() => {
    if (!inSharedRoom || !sharedRoomProject || !roomProjectKey) return null
    const local = currentMeeting?.backlogProjectKey?.trim() ?? ''
    if (local === roomProjectKey) return null
    return sharedRoomProjectMismatchNotice(t, sharedRoomProject.reason, roomProjectKey)
  }, [currentMeeting?.backlogProjectKey, inSharedRoom, roomProjectKey, sharedRoomProject, t])

  useEffect(() => {
    if (!inSharedRoom || !roomProjectKey) return
    const ownId = currentMeeting?.id
    if (!ownId) return
    if ((currentMeeting.backlogProjectKey?.trim() ?? '') === roomProjectKey) return
    updateMeetingInStore(ownId, { backlogProjectKey: roomProjectKey })
  }, [
    currentMeeting?.backlogProjectKey,
    currentMeeting?.id,
    inSharedRoom,
    roomProjectKey,
    updateMeetingInStore,
  ])

  useEffect(() => {
    if (leftSharedRoom || leavingSharedRoom) return
    if (sharedRoom.state.kind !== 'joined') return
    const ownId = currentMeeting?.id
    if (!ownId || currentMeeting?.sharedRoomId) return
    updateMeetingInStore(ownId, { sharedRoomId: sharedRoom.state.roomId })
  }, [
    currentMeeting?.id,
    currentMeeting?.sharedRoomId,
    leftSharedRoom,
    leavingSharedRoom,
    sharedRoom.state,
    updateMeetingInStore,
  ])

  const detectionModalContext = useMemo(() => {
    if (
      sharedRoom.state.kind === 'offered' ||
      sharedRoom.state.kind === 'requested' ||
      sharedRoom.state.kind === 'waiting'
    ) {
      return sharedRoom.state
    }
    if (sharedRoom.lastDismissedMatch) return sharedRoom.lastDismissedMatch
    return null
  }, [sharedRoom.lastDismissedMatch, sharedRoom.state])

  const { showToast: showSharedRoomToast } = sharedRoomUi
  const prevRoomProjectKeyRef = useRef<string | null>(null)
  useEffect(() => {
    if (!inSharedRoom || leftSharedRoom) {
      prevRoomProjectKeyRef.current = null
    }
  }, [inSharedRoom, leftSharedRoom])
  useEffect(() => {
    if (!inSharedRoom || !sharedRoomProject?.key.trim()) return
    const nextKey = sharedRoomProject.key.trim()
    const prevKey = prevRoomProjectKeyRef.current
    if (
      prevKey !== null &&
      prevKey !== nextKey &&
      sharedRoomProject.reason === 'manual' &&
      sharedRoomProject.changedBy?.trim()
    ) {
      showSharedRoomToast(
        sharedRoomProjectManualChangedToast(
          t,
          sharedRoomProject.changedBy.trim(),
          nextKey
        )
      )
    }
    prevRoomProjectKeyRef.current = nextKey
  }, [inSharedRoom, sharedRoomProject, showSharedRoomToast, t])
  const prevOfferKindRef = useRef(sharedRoom.state.kind)

  const emitJoinSuccessToast = useCallback(() => {
    if (joinToastSentRef.current) return
    joinToastSentRef.current = true
    const peerCount =
      sharedRoom.state.kind === 'joined'
        ? countSharedRoomOthers({
            peerNames: sharedRoom.state.peerNames,
            members: sharedRoom.state.meta.members,
          })
        : 0
    showSharedRoomToast(
      peerCount > 0
        ? fmt(t.sharedRoomToastJoined, { count: peerCount })
        : t.sharedRoomToastJoinedSolo
    )
  }, [
    sharedRoom.state,
    showSharedRoomToast,
    t.sharedRoomToastJoined,
    t.sharedRoomToastJoinedSolo,
  ])

  useEffect(() => {
    const kind = sharedRoom.state.kind
    const prev = prevOfferKindRef.current
    if (kind === 'joined' && prev !== 'joined') {
      emitJoinSuccessToast()
    }
    if (kind === 'closed' && (prev === 'waiting' || prev === 'offered' || prev === 'idle')) {
      if (sharedRoom.state.reason === 'expired') {
        showSharedRoomToast(t.sharedRoomRejoinExpired, 'error')
      } else if (sharedRoom.state.reason === 'failed') {
        showSharedRoomToast(t.sharedRoomRejoinFailed, 'error')
      } else if (prev === 'waiting' && sharedRoom.state.reason === 'declined') {
        showSharedRoomToast(t.sharedRoomDeclined, 'error')
      }
    }
    if (kind === 'closed') {
      joinToastSentRef.current = false
    }
    if (kind === 'joined' && sharedRoomMeetingId) {
      clearSharedRoomRejoinSnapshot(sharedRoomMeetingId)
    }
    prevOfferKindRef.current = kind
  }, [
    sharedRoom.state,
    emitJoinSuccessToast,
    sharedRoomMeetingId,
    showSharedRoomToast,
    t.sharedRoomDeclined,
    t.sharedRoomRejoinExpired,
    t.sharedRoomRejoinFailed,
  ])

  /**
   * Everybody in the room can leave it, including the machine that stayed.
   *
   * That machine used to be excluded: its own meeting id IS the room id, so
   * clearing the field on its row left everyone else pointing at a member who
   * had gone. The server re-anchors the room onto a remaining member now
   * (`leaveSharedRoom`), which is what makes this safe — and hiding the button
   * on one machine was what QA read as the room handing out different rights
   * to different people (BRAI-791).
   */
  const canLeaveSharedRoom = sharedRoom.state.kind === 'joined'

  /**
   * One tap, two readers. The room's own history sync and the offer both want
   * every inbound frame, and neither may swallow the other's.
   */
  const handleProxyEvent = useCallback(
    (frame: unknown) => {
      const eventName =
        typeof frame === 'object' && frame !== null
          ? (frame as Record<string, unknown>).event
          : undefined
      const skipRejoinAfterLeave =
        (leftSharedRoomRef.current || leavingSharedRoom) &&
        (eventName === 'sharedroom.auto_joined' || eventName === 'sharedroom.auto_join')
      if (!skipRejoinAfterLeave) {
        // Shared-room offers must run even if room-history sync rejects a frame.
        sharedRoom.handleEvent(frame)
      }
      try {
        recordRoomEvent(frame as Parameters<typeof recordRoomEvent>[0])
      } catch {
        // Deliberately ignored.
      }
    },
    [leavingSharedRoom, recordRoomEvent, sharedRoom]
  )

  const recording = useProxySttRecording({
    source: proxySource,
    targetLang: currentMeeting?.targetLang || settings.targetLang,
    context: {
      ...context,
      ...(contextRef.current ?? {}),
    },
    meetingId: sttRoomId,
    roomId: currentMeeting?.sharedRoomId ?? null,
    // The reader's own row, which `meetingId` stops being the moment they are
    // in a room.
    ownMeetingId: currentMeeting?.id ?? null,
    // `!== false` rather than a plain read: a reader who has run the app
    // before has no stored value, and the offer is on by default.
    autoJoin: settings.autoJoinSharedRoom !== false && !leftSharedRoom,
    projectKey: currentMeeting?.backlogProjectKey ?? null,
    onEvent: handleProxyEvent,
    mixedSources:
      settings.audioSource === 'system'
        ? settings.meetingSetup.hasMicrophone
          ? 'tab+mic'
          : 'tab'
        : 'mic',
    sourceLang: currentMeeting?.sourceLang || settings.sourceLang,
    mode: settings.meetingSetup.mode,
    sttProvider: currentMeeting?.sttProvider || 'soniox',
    // Null for an ordinary meeting, which leaves the field out of the config and
    // keeps that path exactly as it was (BRAI-460). `internal_meeting` never
    // arrives here — it runs on `useInternalMeetingRecording` instead.
    meetingMode: isOfflineSessionKind(sessionKind) ? null : meetingModeForSessionKind(sessionKind),
    getMeetingElapsedMs,
    onTimelineAnchor: handleTimelineAnchor,
    getRealtimeFeatures: getFeatures,
    assistModel: assistModel.getAssistModel(),
    onAssistModelStatus: assistModel.handleStatus,
    onAssistModelError: assistModel.handleError,
    assistRoutes: assistRoutesToWire(assistRoutes.getAssistRoutes()),
    onAssistRoutesStatus: assistRoutes.handleStatus,
    onAssistRoutesError: assistRoutes.handleError,
    onRealtimeFeaturesReady: handleReady,
    onRealtimeFeaturesUpdated: handleUpdated,
    onRealtimeFeaturesError: handleError,
  })
  recordingRef.current = recording

  /**
   * Takes this meeting back out of the room, then continues on its own.
   *
   * The server write comes first: reconnecting to a room the row still
   * names would land the reader straight back where they asked not to be.
   */
  const handleLeaveSharedRoom = useCallback(async (): Promise<
    { ok: true; reconnected: boolean } | { ok: false }
  > => {
    const ownId = currentMeeting?.id
    if (!ownId) return { ok: false }
    // Read before the write below clears it — needed for rememberForRejoin.
    const leavingRoomId = currentMeeting?.sharedRoomId ?? null
    setLeavingSharedRoom(true)
    let soloRoomIdFromLeave: string | null = null
    try {
      const left = await leaveSharedRoom(ownId)
      soloRoomIdFromLeave = left.soloRoomId
    } catch (error) {
      const message = error instanceof Error ? error.message : t.sharedRoomLeaveFailed
      sharedRoomUi.showToast(message, 'error')
      setLeavingSharedRoom(false)
      return { ok: false }
    }
    // Prefer the server-minted solo code. Falling back to ownId only when the
    // meeting was never in a room of its own name and the server returned null
    // (idempotent second press) — reconnecting as ownId is then already safe.
    const nextRoomKey = soloRoomIdFromLeave ?? ownId
    // Ref before reconnect: hook `autoJoin` may still read the pre-leave render;
    // `{ autoJoin: false }` on rejoinSharedRoom is still required (do not remove).
    leftSharedRoomRef.current = true
    const reconnected =
      recordingRef.current?.rejoinSharedRoom(nextRoomKey, { autoJoin: false }) ?? false
    sharedRoom.rememberForRejoin(leavingRoomId)
    sharedRoom.dismiss()
    updateMeetingInStore(ownId, {
      sharedRoomId: null,
      soloRoomId: soloRoomIdFromLeave,
    })
    setLeftSharedRoom(true)
    if (!reconnected) {
      sharedRoomUi.showToast(t.sharedRoomSoloReconnectFailed, 'error')
    }
    setLeavingSharedRoom(false)
    return { ok: true, reconnected }
  }, [
    currentMeeting?.id,
    currentMeeting?.sharedRoomId,
    sharedRoom,
    sharedRoomUi,
    t.sharedRoomSoloReconnectFailed,
    t,
    updateMeetingInStore,
  ])

  const handleDetectionYes = useCallback(() => {
    const dismissingLeft = sharedRoom.lastDismissedMatch?.source === 'left'
    const autoJoinEnabled = settings.autoJoinSharedRoom !== false
    const liveMeetingId = useStore.getState().currentMeetingId
    const liveMeeting = useStore
      .getState()
      .meetings.find((meeting) => meeting.id === liveMeetingId)
    const soloReconnectId =
      liveMeeting?.sharedRoomId || liveMeeting?.soloRoomId || liveMeeting?.id
    if (dismissingLeft) {
      leftSharedRoomRef.current = false
      setLeftSharedRoom(false)
      // Reconnect with autoJoin before rejoin is queued/sent (handoff 84 F2).
      if (soloReconnectId) {
        recordingRef.current?.rejoinSharedRoom(soloReconnectId, { autoJoin: autoJoinEnabled })
      }
    }
    const ok = confirmSharedRoomDetection({
      offerKind: sharedRoom.state.kind,
      hasDismissedSnapshot: Boolean(sharedRoom.lastDismissedMatch),
      acceptOffer: () => sharedRoom.accept(),
      acceptDismissedMatch: () => sharedRoom.acceptDismissedMatch(),
    })
    logActivity('shared_room.join_accepted', {
      meetingId: currentMeeting?.id ?? null,
      projectKey: currentMeeting?.backlogProjectKey ?? null,
      status: ok ? 'success' : 'failure',
    })
    if (!ok) {
      if (dismissingLeft) {
        leftSharedRoomRef.current = true
        setLeftSharedRoom(true)
        recordingRef.current?.clearPendingSharedRoomRejoin()
        if (soloReconnectId) {
          recordingRef.current?.rejoinSharedRoom(soloReconnectId, { autoJoin: false })
        }
      }
      sharedRoomUi.showToast(t.sharedRoomFailed, 'error')
      return
    }
    // Stay on the waiting copy inside the modal until auto_join or the 15s timeout.
  }, [
    currentMeeting?.backlogProjectKey,
    currentMeeting?.id,
    settings.autoJoinSharedRoom,
    sharedRoom,
    sharedRoomUi,
    t.sharedRoomFailed,
  ])

  /** Apply the modal dismissal without treating every close or Cancel as an explicit No. */
  const dismissSharedRoomDetection = useCallback(() => {
    let ok = true
    if (sharedRoom.state.kind === 'offered') {
      ok = sharedRoom.decline()
      sharedRoomUi.showToast(t.sharedRoomToastContinuing)
    } else if (sharedRoom.state.kind === 'requested') {
      ok = sharedRoom.decline()
    } else if (sharedRoom.state.kind === 'waiting') {
      ok = sharedRoom.withdrawFromMerge()
      sharedRoomUi.showToast(t.sharedRoomToastContinuing)
    }
    sharedRoomUi.closeDetectionModal()
    return ok
  }, [sharedRoom, sharedRoomUi, t.sharedRoomToastContinuing])

  const handleDetectionNo = useCallback(() => {
    const ok = dismissSharedRoomDetection()
    logActivity('shared_room.join_declined', {
      meetingId: currentMeeting?.id ?? null,
      projectKey: currentMeeting?.backlogProjectKey ?? null,
      status: ok ? 'success' : 'failure',
    })
  }, [
    currentMeeting?.backlogProjectKey,
    currentMeeting?.id,
    dismissSharedRoomDetection,
  ])

  const handleConfirmLeaveSharedRoom = useCallback(async () => {
    const result = await handleLeaveSharedRoom()
    if (!result.ok) return
    sharedRoomUi.closeLeaveConfirm()
    sharedRoomUi.showToast(t.sharedRoomToastLeft)
  }, [handleLeaveSharedRoom, sharedRoomUi, t.sharedRoomToastLeft])

  const nativeTranscription = useNativeTranscriptSession({
    targetLang: currentMeeting?.targetLang || settings.targetLang,
    sourceLang: currentMeeting?.sourceLang || settings.sourceLang,
    mode: settings.meetingSetup.mode,
    sttProvider: currentMeeting?.sttProvider || 'soniox',
    projectKey: currentMeeting?.backlogProjectKey ?? null,
    context: {
      ...context,
      ...(contextRef.current ?? {}),
    },
    getMeetingElapsedMs,
    onTimelineAnchor: handleTimelineAnchor,
    getRealtimeFeatures: getFeatures,
    getAssistModel: assistModel.getAssistModel,
    onAssistModelStatus: assistModel.handleStatus,
    onAssistModelError: assistModel.handleError,
    getAssistRoutes: () => assistRoutesToWire(assistRoutes.getAssistRoutes()),
    onAssistRoutesStatus: assistRoutes.handleStatus,
    onAssistRoutesError: assistRoutes.handleError,
    onRealtimeFeaturesReady: handleReady,
    onRealtimeFeaturesUpdated: handleUpdated,
    onRealtimeFeaturesError: handleError,
  })
  featureSendRef.current = (features) => {
    const fromRecording = recording.sendFeaturesUpdate(features)
    const fromNative = nativeTranscription.sendFeaturesUpdate(features)
    return fromRecording || fromNative
  }
  // Both, for the reason the pane switches go to both: in a Teams or Meet
  // meeting every speaker has a socket of their own, and one that was not told
  // would go on answering with the model the panel stopped showing.
  assistModelSendRef.current = (model) => {
    const fromRecording = recording.sendAssistModelUpdate(model)
    const fromNative = nativeTranscription.sendAssistModelUpdate(model)
    return fromRecording || fromNative
  }
  assistRoutesSendRef.current = (routes) => {
    const fromRecording = recording.sendAssistRoutesUpdate(routes)
    const fromNative = nativeTranscription.sendAssistRoutesUpdate(routes)
    return fromRecording || fromNative
  }
  const {
    start: startScreenCapture,
    pause: pauseScreenCapture,
    resume: resumeScreenCapture,
    stop: stopScreenCapture,
  } = useScreenCapture()
  const sendScreenFrameRef = useRef(recording.sendScreenFrame)
  sendScreenFrameRef.current = recording.sendScreenFrame
  const startNativeTranscription = nativeTranscription.start
  const nativeMode =
    settings.meetingSetup.transcriptionSource === 'teams-native' ||
    settings.meetingSetup.transcriptionSource === 'google-meet-native'
  /** Assist uses the proxy STT WebSocket — not available in Teams/Google native transcription. */
  const copilotAssistEnabled = ASSIST_PANEL_UI_ENABLED && !nativeMode
  // Kept in the tree for the length of its own slide, so closing the panel is
  // a movement rather than a disappearance.
  const copilotPanel = usePanelPresence(
    copilotAssistEnabled && isCopilotOpen,
    COPILOT_PANEL_TRANSITION_MS
  )
  const meetingProjectKey = currentMeeting?.backlogProjectKey ?? null
  const sendProjectUpdate = recording.sendProjectUpdate
  const sendAssistAsk = recording.sendAssistAsk
  const appliedBacklogAccountRef = useRef<number | null>(null)
  const [connectOpen, setConnectOpen] = useState(false)
  /**
   * Whether the reader has gone and done something about Backlog from here.
   *
   * It gates asking questions again: the account becoming known is also what
   * happens on an ordinary panel open for somebody who connected weeks ago,
   * and re-asking a resumed meeting's old refusals because a panel was opened
   * would be a surprise nobody asked for.
   */
  const backlogTouchedRef = useRef(false)
  const openBacklogConnect = useCallback(() => {
    backlogTouchedRef.current = true
    logActivity('copilot.backlog_connect_opened')
    setConnectOpen(true)
  }, [])
  // Defined further down, where the card list it reads exists; held in a ref
  // because the two callbacks that fire it are built before that point.
  const retryRefusedQuestionsRef = useRef<(prerequisites: BacklogPrerequisites) => void>(
    () => undefined
  )

  /**
   * Whether the Copilot may offer to fix its own prerequisites (BRAI-689).
   *
   * Online browser meetings only. A shared room exposes the same project
   * picker as an ordinary meeting; its recorder applies the project to the
   * room-wide Copilot flow. `internal_meeting` still takes its project from
   * `config` and will not be told otherwise.
   *
   * Interview and Presentation are still online browser meetings. Using
   * `meetingModeForSessionKind(...) === null` here accidentally treated every
   * non-default mode as offline and hid the project picker from both.
   */
  const copilotSetupEnabled =
    copilotAssistEnabled && isCopilotOpen && !isOfflineSessionKind(sessionKind)

  /**
   * Put a project on the meeting, and on the session already running.
   *
   * The row is written first on purpose: the server reads it for on-demand
   * minutes and for post-meeting learning, and a row still naming the old
   * project costs the minutes their title and date.
   */
  const chooseCopilotProject = useCallback(
    async (projectKey: string): Promise<ChooseProjectResult> => {
      const meetingId = currentMeeting?.id
      if (!meetingId) return 'failed'
      try {
        await updateMeeting(meetingId, { backlogProjectKey: projectKey })
      } catch {
        logActivity('copilot.project_chosen', { meetingId, projectKey, status: 'failure' })
        return 'failed'
      }
      updateMeetingInStore(meetingId, { backlogProjectKey: projectKey })
      logActivity('copilot.project_chosen', { meetingId, projectKey })
      // `saved` rather than a failure when nothing was sent: a meeting that is
      // not recording has no session to miss the change, and warning about one
      // would be inventing a problem.
      const result = sendProjectUpdate({ projectKey }) ? 'applied' : 'saved'
      retryRefusedQuestionsRef.current({
        connected: appliedBacklogAccountRef.current !== null,
        hasProject: true,
      })
      return result
    },
    [currentMeeting?.id, sendProjectUpdate, updateMeetingInStore]
  )

  /**
   * Tell the running session about a Backlog account it did not start with.
   *
   * Connecting happens mid-meeting, long after `config` went out, so a session
   * opened by a reader with no grant goes on answering "connect your Backlog
   * account" until it is told otherwise — which is the bug BRAI-689 was raised
   * for. Sent once per account: re-sending the one already in force is a no-op
   * the server answers `changed: false`, but it is still a frame nobody needs.
   */
  const applyBacklogAccount = useCallback(
    (accountId: number) => {
      if (appliedBacklogAccountRef.current === accountId) return
      appliedBacklogAccountRef.current = accountId
      sendProjectUpdate({ userIdBacklog: accountId })
      // Covers the approval that happened in an ordinary tab, where no message
      // comes back and this reading is the only sign it worked. The dialog's
      // own report covers the usual path; whichever lands first takes the
      // questions with it, so the other finds nothing left to do.
      if (!backlogTouchedRef.current) return
      retryRefusedQuestionsRef.current({
        connected: true,
        hasProject: Boolean(meetingProjectKeyRef.current?.trim()),
      })
    },
    [sendProjectUpdate]
  )

  /** Read inside a callback that was built before the meeting could change. */
  const meetingProjectKeyRef = useRef(meetingProjectKey)
  meetingProjectKeyRef.current = meetingProjectKey

  const lastProjectUpdateErrorCodeRef = useRef<string | null>(null)
  useEffect(() => {
    const code = recording.projectUpdateErrorCode
    if (!code || recording.projectUpdateStatus !== 'error') return
    if (lastProjectUpdateErrorCodeRef.current === code) return
    lastProjectUpdateErrorCodeRef.current = code
    if (code === 'not_supported') {
      showSharedRoomToast(t.projectUpdateNotSupported, 'error')
    } else if (code === 'project_not_readable') {
      showSharedRoomToast(t.projectUpdateNotReadable, 'error')
    }
  }, [
    recording.projectUpdateErrorCode,
    recording.projectUpdateStatus,
    showSharedRoomToast,
    t,
  ])

  /**
   * Ask the Copilot, and count which kind of asking it was (BRAI-677).
   *
   * A question somebody typed and a suggestion they pressed are two different
   * uses of this panel — one says the reader had something of their own to
   * ask, the other that the AI's offer was worth taking — and a single "asked"
   * count would answer neither.
   */
  const askCopilot = useCallback(
    (input: AssistAskInput) => {
      if (!assistOn) return false
      const sent = sendAssistAsk(input)
      logActivity('text' in input ? 'copilot.question_asked' : 'copilot.suggestion_asked', {
        meetingId: currentMeeting?.id ?? null,
        projectKey: meetingProjectKeyRef.current,
        status: sent ? 'success' : 'failure',
      })
      return sent
    },
    [assistOn, currentMeeting?.id, sendAssistAsk]
  )

  /** Report the grant the dialog has just obtained, before any project. */
  const handleBacklogConnected = useCallback(() => {
    retryRefusedQuestionsRef.current({
      connected: true,
      hasProject: Boolean(meetingProjectKeyRef.current?.trim()),
    })
  }, [])

  /** Adapts the strip's three-way result to the dialog's yes-or-no. */
  const chooseProjectFromDialog = useCallback(
    async (projectKey: string) => (await chooseCopilotProject(projectKey)) !== 'failed',
    [chooseCopilotProject]
  )
  const nativeActive =
    nativeTranscription.session !== null &&
    !['COMPLETED', 'FAILED', 'REVOKED'].includes(nativeTranscription.session.status)
  const nativeSessionCompleted =
    nativeMode && nativeTranscription.session?.status === 'COMPLETED'
  recordingFinalTokensRef.current = recording.finalTokens
  nativeFinalTokensRef.current = nativeTranscription.tokens.filter((token) => token.is_final)

  // Placed after the recording hook rather than beside the first read, because
  // it waits on that hook's state. `recording` is what feeds the room its
  // history in the first place — the native path posts none — so its socket is
  // the one whose opening leaves the gap.
  useEffect(() => {
    if (!sharedRoomId || recording.state !== 'recording') return undefined
    const timer = setTimeout(() => {
      // Claimed here rather than before the timer: a `state` that flickers away
      // and back would otherwise clear the pending timer having already marked
      // the room done, and the gap would never be read at all.
      if (closedRoomGapIdsRef.current.has(sharedRoomId)) return
      closedRoomGapIdsRef.current.add(sharedRoomId)
      void closeRoomHistoryGap(sharedRoomId)
    }, ROOM_HISTORY_GAP_DELAY_MS)
    return () => clearTimeout(timer)
  }, [sharedRoomId, recording.state, closeRoomHistoryGap])

  const recordingMemoRef = useRef(recording.memo)
  recordingMemoRef.current = recording.memo

  const recordingStartRef = useRef(recording.start)
  recordingStartRef.current = recording.start
  // Closing the STT socket is all that is left to do here since the minutes
  // moved to the server (BRAI-412). Held in a ref because it runs after this
  // component has navigated away and unmounted. Native mode has no socket of
  // its own on `recording` — its session was already stopped in `prepareSave`
  // — so this is a no-op there.
  const recordingStopRef = useRef(recording.stop)
  recordingStopRef.current = recording.stop
  const recordingCostSummaryRef = useRef(recording.costSummaryRef)
  recordingCostSummaryRef.current = recording.costSummaryRef
  const nativeReadCostSummaryRef = useRef(nativeTranscription.readCostSummary)
  nativeReadCostSummaryRef.current = nativeTranscription.readCostSummary
  const shouldRunSessionTimer = nativeMode
    ? nativeActive
    : shouldRunRecordingTimer(recording.state, recording.isActive)
  const shouldKeepSessionStatus = nativeMode
    ? nativeActive
    : shouldPreserveRecordingSessionStatus(recording.state, recording.isActive)

  // Ship the recording to the server as it goes, so closing this tab mid-meeting
  // no longer loses everything recorded so far (BRAI-457). Both capture modes go
  // through an `ArchiveAudioRecorder`, so one hook covers them.
  const nativeAudioArchiveRef = useRef(nativeTranscription.takeUnsentAudioChunks)
  nativeAudioArchiveRef.current = nativeTranscription.takeUnsentAudioChunks
  usePeriodicAudioPartUpload({
    meetingId: currentMeeting?.id ?? null,
    enabled: shouldRunSessionTimer,
    takeUnsentChunks: () => {
      // Called on the recorder rather than read off it and called bare. How
      // far the periodic upload has got is counted on the instance, a class
      // body is strict mode, and a detached call therefore arrives with
      // `this` undefined and throws on its first line — every tick, before
      // anything is uploaded. A normal meeting shipped no audio at all while
      // that was so, which is the whole of what this hook is for.
      if (!nativeMode) return archiveRecorderRef.current.takeUnsentChunks()
      // The native side is a plain closure with no `this` to lose. The guard
      // is for its absence instead: not every double of that hook has the
      // function, and this screen would call it regardless.
      const takeUnsentAudioChunks = nativeAudioArchiveRef.current
      return typeof takeUnsentAudioChunks === 'function' ? takeUnsentAudioChunks() : null
    },
    getDurationSeconds: () => elapsedSecondsRef.current,
  })

  // Close the realtime-context modal once the server confirms the update
  // (`context.updated` → `contextUpdateStatus === 'success'`) and remember
  // the saved value so reopening the modal shows what's currently in effect
  // instead of a blank textarea. Also surfaces a brief "saved" toast near the
  // control-bar trigger once the modal is closed (see `justUpdatedContext`).
  // `contextCharCount` is snapshot into `contextSavedCharCount` here because
  // the hook's own value can move on to reflect a later update by the time
  // the toast renders. Leave the modal open (and the draft intact) on
  // `context.update.error` so the user can see the failure and retry.
  // `sendContextUpdate` resets status to `pending` before each send so this
  // effect still re-runs when a later ack returns the same char count
  // (same length, different text).
  useEffect(() => {
    if (recording.contextUpdateStatus !== 'success') return
    setIsContextPanelOpen(false)
    setSavedContextValue(contextInputValueRef.current)
    setContextSavedCharCount(recording.contextCharCount)
    setJustUpdatedContext(true)
  }, [recording.contextUpdateStatus, recording.contextCharCount])

  /**
   * Closes the realtime-context modal, discarding any unsaved edits by
   * reverting the draft back to the last saved value. Shared by the header X
   * button, backdrop click, Escape key, and the Cancel button so every
   * dismissal path behaves identically (BRAI-213 redesign).
   */
  const handleCloseContextModal = useCallback(() => {
    setIsContextPanelOpen(false)
    setContextInputValue(savedContextValue)
  }, [savedContextValue])

  /** Opens the realtime-context modal, or closes it (reverting unsaved edits) if already open. */
  /**
   * Asks the AI for a hard translation of the speech so far, from the button or
   * from the space bar. Browser-captured meetings only: a native meeting's audio
   * runs over the participant sockets, which have no such command.
   */
  const handleTranslationHard = useCallback(() => {
    const sent = recording.sendTranslationHard()
    showSaveToast({
      kind: sent ? 'success' : 'error',
      message: sent ? t.translationHardSent : t.translationHardFailed,
    })
  }, [recording, showSaveToast, t.translationHardFailed, t.translationHardSent])

  // Native meetings stream over the participant sockets, which carry no such
  // command, so there is nothing for this control to send there. The session
  // states match the realtime-context control's (`isContextSessionReady`) —
  // spelled out here because this has to sit above the `!currentMeeting` early
  // return, and a hook may not be called conditionally.
  const canRequestTranslationHard =
    TRANSLATION_HARD_ENABLED &&
    !nativeMode &&
    (recording.state === 'recording' || recording.state === 'paused')
  // Mid-call invite (same Join-room path as Create Meeting). Show whenever the
  // live session is up — including connecting / interrupted / draining — so the
  // host is not stuck without Invite before STT fully settles on `recording`.
  // Native sessions have no recording states; `nativeActive` is that window.
  const canInviteMembers = nativeMode
    ? nativeActive
    : recording.isActive ||
      recording.state === 'interrupted' ||
      recording.state === 'draining'
  useSpaceHotkey({
    // A modal owns the keyboard while it is open — the context editor's own
    // textarea is a space bar's rightful owner, and the feedback dialog is modal.
    enabled: canRequestTranslationHard && !isContextPanelOpen && !feedbackOpen,
    onPress: handleTranslationHard,
    triggerRef: translationHardButtonRef,
  })

  const handleToggleContextModal = useCallback(() => {
    if (isContextPanelOpen) {
      handleCloseContextModal()
    } else {
      setIsContextPanelOpen(true)
    }
  }, [isContextPanelOpen, handleCloseContextModal])

  /** Auto-dismisses the "context saved" toast 5s after a successful update. */
  useEffect(() => {
    if (!justUpdatedContext) return
    const timer = window.setTimeout(() => setJustUpdatedContext(false), 5000)
    return () => window.clearTimeout(timer)
  }, [justUpdatedContext])

  // The language picker has to address whichever pipeline is actually translating: a browser
  // meeting has `recording`'s own STT socket, a Teams meeting translates on the per-participant AI
  // streams and never starts `recording` at all — so routing everything through `recording` left
  // the change with nowhere to go and the meeting kept translating into the old language
  // (BRAI-359).
  const hasLiveTranslationSession = nativeMode ? nativeActive : recording.isActive
  const languageUpdateStatus = nativeMode
    ? nativeTranscription.languageUpdateStatus
    : recording.languageUpdateStatus
  const languageUpdateError = nativeMode
    ? nativeTranscription.languageUpdateError
    : recording.languageUpdateError
  const sendNativeLanguageUpdate = nativeTranscription.sendLanguageUpdate

  /** Switches translation language on the live socket without reconnecting. */
  const handleTargetLangChange = useCallback(
    (nextTargetLang: string) => {
      if (!currentMeeting || nextTargetLang === currentMeeting.targetLang || isChangingTargetLang) {
        return
      }

      const previousTargetLang = currentMeeting.targetLang
      if (!hasLiveTranslationSession) {
        setTargetLang(nextTargetLang)
        targetLangRef.current = nextTargetLang
        return
      }

      const sent = nativeMode
        ? sendNativeLanguageUpdate(nextTargetLang)
        : recording.sendLanguageUpdate(nextTargetLang)
      if (!sent) return

      revertTargetLangRef.current = previousTargetLang
      setIsChangingTargetLang(true)
      setTargetLang(nextTargetLang)
      targetLangRef.current = nextTargetLang
    },
    [
      currentMeeting,
      hasLiveTranslationSession,
      isChangingTargetLang,
      nativeMode,
      recording,
      sendNativeLanguageUpdate,
      setTargetLang,
    ]
  )

  useEffect(() => {
    if (!isChangingTargetLang) return

    if (languageUpdateStatus === 'success') {
      revertTargetLangRef.current = null
      setIsChangingTargetLang(false)
      return
    }

    if (languageUpdateStatus === 'error') {
      const revertTo = revertTargetLangRef.current
      if (revertTo) {
        setTargetLang(revertTo)
        targetLangRef.current = revertTo
      }
      revertTargetLangRef.current = null
      showSaveToast({
        kind: 'error',
        message:
          languageUpdateError ??
          fmt(t.languageSwitchFailed, {
            language: getLanguage(revertTo ?? currentMeeting?.targetLang ?? '').name,
          }),
      })
      setIsChangingTargetLang(false)
      return
    }

    // The session the command was sent on is gone, so no acknowledgment is coming. Release the
    // picker rather than leaving it disabled for the rest of the meeting.
    const sessionEnded = nativeMode
      ? !nativeActive
      : recording.state === 'error' || recording.state === 'canceled'
    if (sessionEnded) {
      revertTargetLangRef.current = null
      setIsChangingTargetLang(false)
    }
  }, [
    currentMeeting?.targetLang,
    isChangingTargetLang,
    languageUpdateError,
    languageUpdateStatus,
    nativeActive,
    nativeMode,
    recording.state,
    setTargetLang,
    showSaveToast,
    t.languageSwitchFailed,
  ])

  /** Releases a pending language picker when an older backend never acknowledges the command. */
  useEffect(() => {
    if (!isChangingTargetLang || languageUpdateStatus !== 'pending') return

    const timer = window.setTimeout(() => {
      revertTargetLangRef.current = null
      setIsChangingTargetLang(false)
      showSaveToast({ kind: 'error', message: t.languageSwitchTimeout })
    }, 5000)

    return () => window.clearTimeout(timer)
  }, [isChangingTargetLang, languageUpdateStatus, showSaveToast, t.languageSwitchTimeout])

  /** Toggles microphone mute and mirrors that state to the active capture source. */
  const toggleMicMute = useCallback(() => {
    if (!hasMicrophoneRef.current) return
    setIsMicMuted((muted) => {
      const nextMuted = !muted
      applyMicMuted(nextMuted)
      return nextMuted
    })
  }, [applyMicMuted])

  /** Starts capture and STT streaming, preparing shared tab audio when needed. */
  /**
   * Resumes a session that network loss interrupted, instead of starting a new
   * one: the capture, the transcript and the archive recording are all still
   * there, so a fresh start would discard them.
   */
  const handleResumeAfterInterruption = useCallback(() => {
    // The Summary is the one thing `reconnect()` does NOT carry over. `start()`
    // keeps the tokens for a resume (`if (!resumingCapture)`) but resets `memo`
    // unconditionally, and the reopened socket is a new session on the AI
    // service whose first `summarize.result` is a full-replace snapshot built
    // from post-outage speech alone. Archiving here is what keeps the pre-outage
    // narrative on screen through both of those (BRAI-369) — the same move the
    // restart path already makes for the same reason (BRAI-178).
    captureArchivedMemo(mergeArchivedMemo(archivedMemoRef.current, recording.memo))
    archiveRecorderRef.current.resume()
    recording.reconnect()
    resumeScreenCapture()
  }, [captureArchivedMemo, recording, resumeScreenCapture])

  const handleStartMeeting = useCallback(async () => {
    if (isStartingMeeting || nativeActive || recording.isActive || recording.state === 'connecting')
      return

    setIsStartingMeeting(true)
    setDismissedError(null)
    setCompletedDurationSeconds(null)
    preparedSaveRef.current = null
    setStartError(null)
    setSaveError(null)

    try {
      if (nativeMode) {
        if (!currentMeeting) throw new Error('Meeting not found')
        await seedArchiveFromStoredTranscript(currentMeeting.id)
        await startNativeTranscription({
          meetingId: currentMeeting.id,
          provider:
            settings.meetingSetup.transcriptionSource === 'teams-native'
              ? 'MICROSOFT_TEAMS'
              : 'GOOGLE_MEET',
          meetingReference: settings.meetingSetup.nativeMeetingReference,
        })
        return
      }
      // A prior session (e.g. ended by the browser's native "Stop sharing")
      // may have left tokens behind that the STT hook is about to clear on
      // restart. Archive them so the transcript stays continuous, then clear
      // the hook buffers immediately — before any await — so Output does not
      // render archived + still-active copies while the share picker is open
      // (or permanently if the picker is cancelled and Start is pressed again).
      appendArchivedTokens([
        ...recording.finalTokens,
        ...recording.partialTokens,
        ...recording.batchTokens,
      ])
      appendArchivedLiveTurns(recording.liveTurns ?? [])
      captureArchivedMemo(mergeArchivedMemo(archivedMemo, recording.memo))
      // The Copilot panel is cleared by the same restart (setAssistCards([]) in
      // the hook's start()), and it reads archived ∪ live — so without this the
      // whole conversation disappears the moment the reader shares again.
      // Pending answers are dropped rather than archived: the socket that would
      // have answered them is gone, so a kept one spins for good.
      captureArchivedAssistCards(
        (recording.assistCards ?? []).filter((card) => !isPendingAnswerCard(card))
      )
      recording.clearTranscriptBuffers()

      const shareTabAudio =
        settings.audioSource === 'system' && settings.meetingSetup.shareTabAudio

      if (shareTabAudio && !hasPreparedTabAudioCapture()) {
        clearActiveTabAudioCapture()
        await prepareTabAudioCapture(
          settings.meetingSetup.microphoneDeviceId,
          settings.meetingSetup.hasMicrophone
        )
      }

      // Fire-and-forget: STT start() returns immediately; audio source starts later on `ready`.
      void Promise.resolve(recordingStartRef.current())

      // Best-effort screen capture — never abort STT/meeting on failure.
      //
      // Only ever borrows the display stream the user already granted for tab
      // audio. A mic-only meeting gets no screen capture at all: opening a
      // second picker there asked somebody who had just declined sharing to
      // pick a screen anyway, so declining changed which prompt appeared rather
      // than removing it (BRAI-352).
      //
      // Off unless the create form asked for it. This used to run on every
      // share-tab meeting with nothing on screen saying a screenshot of the tab
      // was going to the AI every few seconds.
      const sendScreenFrames = settings.meetingSetup.sendScreenFrames
      const tabDisplayStream =
        shareTabAudio && sendScreenFrames ? getTabAudioDisplayStream() : null
      const hasLiveTabVideo = tabDisplayStream
        ?.getVideoTracks()
        .some((track) => track.readyState === 'live')
      if (currentMeeting && tabDisplayStream && hasLiveTabVideo) {
        try {
          await startScreenCapture({
            meetingId: currentMeeting.id,
            stream: tabDisplayStream,
            // Borrowed: the tab-audio capture owns these tracks and stops them.
            ownsStream: false,
            sendFrame: (message) => sendScreenFrameRef.current(message),
            onError: (error) => {
              console.warn('[screen-capture]', error.message)
            },
          })
        } catch (error) {
          console.warn('[screen-capture] failed to start', error)
        }
      }
      logActivity('meeting.started', {
        meetingId: currentMeeting?.id ?? null,
        projectKey: currentMeeting?.backlogProjectKey ?? null,
      })
    } catch (error) {
      logActivity('meeting.started', {
        meetingId: currentMeeting?.id ?? null,
        projectKey: currentMeeting?.backlogProjectKey ?? null,
        status: 'failure',
      })
      setStartError(error instanceof Error ? error.message : t.failedStartMeeting)
    } finally {
      setIsStartingMeeting(false)
    }
  }, [
    appendArchivedTokens,
    appendArchivedLiveTurns,
    archivedMemo,
    captureArchivedAssistCards,
    captureArchivedMemo,
    currentMeeting,
    isStartingMeeting,
    nativeActive,
    nativeMode,
    seedArchiveFromStoredTranscript,
    startScreenCapture,
    startNativeTranscription,
    recording.assistCards,
    recording.batchTokens,
    recording.clearTranscriptBuffers,
    recording.finalTokens,
    recording.isActive,
    recording.liveTurns,
    recording.memo,
    recording.partialTokens,
    recording.state,
    settings.audioSource,
    settings.meetingSetup.nativeMeetingReference,
    settings.meetingSetup.transcriptionSource,
    settings.meetingSetup.microphoneDeviceId,
    settings.meetingSetup.shareTabAudio,
    settings.meetingSetup.sendScreenFrames,
    settings.meetingSetup.hasMicrophone,
    t.failedStartMeeting,
  ])

  useEffect(() => {
    if (recording.isActive) {
      applyMicMuted(isMicMuted)
    }
  }, [applyMicMuted, isMicMuted, recording.isActive])

  // Stop screen capture when the browser STT session ends unexpectedly
  // (error, user stop-sharing on audio, canceled, etc.). End-meeting only
  // pauses capture so Cancel can resume; terminal STT states still stop it.
  useEffect(() => {
    if (nativeMode) return
    if (
      recording.state === 'stopped' ||
      recording.state === 'canceled' ||
      recording.state === 'error'
    ) {
      stopScreenCapture()
    }
  }, [nativeMode, recording.state, stopScreenCapture])

  /**
   * Tears the live session down the moment the browser goes offline.
   *
   * The offline banner alone was misleading: the socket was left open and the
   * transport keeps reporting the session as active, so the control stayed on
   * Pause and read as "still recording, just paused" — while nothing was
   * reaching the server. Dropping the session flips `isActive`/`nativeActive`
   * off, which turns the control back into Start, the honest state.
   *
   * `cancel` rather than `stop`: stopping waits on the proxy to flush final
   * events, which cannot arrive with no network. Already-transcribed tokens
   * survive either way — teardown never clears them — so the transcript stays on
   * screen and is archived normally on the next start.
   */
  const dropLiveSessionRef = useRef<() => void>(() => {})
  dropLiveSessionRef.current = () => {
    if (nativeMode) {
      if (nativeActive) nativeTranscription.dropConnection()
      return
    }
    if (recording.isActive || recording.state === 'connecting') {
      // `dropSocketForOffline` rather than `cancel`: cancel stops the audio
      // source, which ends the mixed stream, which ends the MediaRecorder
      // holding this meeting's audio — and the next start then cleared every
      // chunk recorded before the outage (BRAI-366). Pausing the recorder on
      // top keeps the file free of a long silent stretch while still leaving it
      // one continuous WebM.
      recording.dropSocketForOffline()
      archiveRecorderRef.current.pause()
      pauseScreenCapture()
    }
  }
  useEffect(() => {
    if (!isOnline) dropLiveSessionRef.current()
  }, [isOnline])

  // Mute resets between meetings, so the next one does not start silently
  // muted — but NOT while the session is `interrupted`. That state is the
  // middle of a meeting whose socket dropped: the capture is still running and
  // `reconnect` reuses it, so clearing the switch here would hand the
  // reconnected session a live microphone, which is BRAI-403 again by way of a
  // network blip. `isActive` excludes `interrupted`, so it cannot carry this on
  // its own.
  useEffect(() => {
    if (!recording.isActive && recording.state !== 'interrupted') {
      setIsMicMuted(false)
    }
  }, [recording.isActive, recording.state])

  useEffect(() => {
    if (!shouldKeepSessionStatus) {
      // A completed native call is finalized by the effect below. Preserve its
      // clock until `handleEndMeeting` snapshots the duration for saving.
      if (nativeSessionCompleted || isEndingMeeting || feedbackOpen) return
      setElapsedSeconds(0)
      elapsedSecondsRef.current = 0
      meetingStartedAtRef.current = null
      setDurationWarningStep('closed')
      setNextWarningAtSeconds(DURATION_WARNING_FIRST_AT_SECONDS)
      durationWarningActionLockRef.current = false
      return
    }

    if (!shouldRunSessionTimer) return

    if (meetingStartedAtRef.current === null) {
      meetingStartedAtRef.current = Date.now()
    }

    const startedAt = meetingStartedAtRef.current
    const interval = window.setInterval(() => {
      const nextElapsedSeconds = Math.floor((Date.now() - startedAt) / 1000)
      elapsedSecondsRef.current = nextElapsedSeconds
      setElapsedSeconds(nextElapsedSeconds)
    }, 1000)

    return () => window.clearInterval(interval)
  }, [
    feedbackOpen,
    isEndingMeeting,
    nativeSessionCompleted,
    recording.state,
    shouldKeepSessionStatus,
    shouldRunSessionTimer,
  ])

  useEffect(() => {
    const pendingId = settings.pendingStartMeetingId
    const alreadyAttempted =
      autoStartAttemptRef.current?.meetingId === currentMeetingId &&
      autoStartAttemptRef.current?.pendingId === pendingId

    const shouldAutoStart =
      currentMeetingId !== null &&
      pendingId === currentMeetingId &&
      !alreadyAttempted &&
      !recording.isActive &&
      recording.state !== 'connecting' &&
      recording.state !== 'stopping'

    if (!shouldAutoStart) return

    // `setTimeout` (not `requestAnimationFrame`) so the auto-start still runs
    // when this tab is in the background — the browser's native share-tab
    // picker often shifts focus to the shared tab right after "Share" is
    // clicked, and `requestAnimationFrame` callbacks are suspended entirely
    // while a tab is backgrounded, which left the STT socket never connecting
    // until the user manually returned to this tab.
    const timeoutId = setTimeout(() => {
      autoStartAttemptRef.current = { meetingId: currentMeetingId, pendingId }
      void Promise.resolve(handleStartMeeting()).catch(() => {
        /* The recording hook exposes the error state in the UI. */
      })
    }, 0)

    return () => clearTimeout(timeoutId)
  }, [
    currentMeetingId,
    handleStartMeeting,
    recording.isActive,
    recording.state,
    settings.pendingStartMeetingId,
  ])

  useEffect(() => {
    if (
      (nativeActive || recording.isActive) &&
      currentMeetingId !== null &&
      settings.pendingStartMeetingId === currentMeetingId
    ) {
      clearPendingStartMeeting()
    }
  }, [
    clearPendingStartMeeting,
    currentMeetingId,
    recording.isActive,
    nativeActive,
    settings.pendingStartMeetingId,
  ])

  useEffect(() => {
    // Native mode has no `recording` state machine of its own (browser STT's
    // `recording.state` never leaves its initial value here), so a failed
    // `startNativeTranscription()` call is only observable via `startError` —
    // mirror the browser-mode error/canceled check with that instead.
    const failedToStart = nativeMode
      ? startError !== null
      : recording.state === 'error' || recording.state === 'canceled'
    if (
      !failedToStart ||
      currentMeetingId === null ||
      settings.pendingStartMeetingId !== currentMeetingId
    ) {
      return
    }

    const attempt = autoStartAttemptRef.current
    if (attempt?.meetingId === currentMeetingId && attempt.pendingId === currentMeetingId) {
      clearPendingStartMeeting()
    }
  }, [
    clearPendingStartMeeting,
    currentMeetingId,
    nativeMode,
    recording.state,
    settings.pendingStartMeetingId,
    startError,
  ])

  // `archivedTokens` (state) is always reassigned to the exact same array as
  // `archivedTokensRef.current` inside `appendArchivedTokens`, so it doubles
  // as the dependency that tells `useMemo` when the ref actually changed.
  // Without this memo, the once-a-second `elapsedSeconds` tick (see the timer
  // effect above) re-renders this component and rebuilds these arrays from
  // scratch, forcing `Renderer` to re-render the entire transcript every
  // second even though no new token arrived.
  // Only Azure and gpt-realtime-whisper have NO inline realtime translation
  // at all — their translation arrives exclusively via `translation.batch`
  // (`recording.batchTokens`). Soniox and google_live_translate both already
  // stream their translation live into `finalTokens` (`translation.final`/
  // `translation.partial`) but ALSO happen to emit `translation.batch` as a
  // redundant second stream — mixing that into the Output pane too replayed
  // content that was already shown live (reported as translated text
  // resetting and re-typing itself once a live partial finalized).
  const isBatchOnlyTranslationProvider =
    currentMeeting?.sttProvider === 'azure' ||
    currentMeeting?.sttProvider === 'gpt-realtime-whisper'
  const displayTokens = useMemo(
    () =>
      nativeMode
        ? // The archive is what carries a previous session's transcript, and the
          // native path used to drop it on the floor — the panes restarted empty
          // whenever the bot had to rejoin (BRAI-447).
          [...archivedTokens, ...nativeTranscription.tokens]
        : [...archivedTokens, ...recording.finalTokens, ...recording.partialTokens],
    [
      archivedTokens,
      nativeMode,
      nativeTranscription.tokens,
      recording.finalTokens,
      recording.partialTokens,
    ]
  )
  // In `one_way` mode a single speaker/language pair is translated in a fixed
  // direction, so `translation_status` alone tells us which pane a token
  // belongs to. In `two_way` mode both participants speak (and get
  // translated into) different languages, so each side's pane can receive
  // both `original` and `translation` tokens depending on who's talking —
  // filtering by `translation_status` there would mix both speakers' partial
  // and final tokens into the same pane (both carry `translation_status:
  // 'original'` while they're talking). Split by `token.language` instead —
  // for every token, partial or final — so each pane always shows a single,
  // fixed language; tokens missing a `language` (rare) fall back to the
  // `translation_status` split.
  const isTwoWayMode = settings.meetingSetup.mode === 'two_way'
  const sourceLangForSplit = currentMeeting?.sourceLang || settings.sourceLang
  // Native Input includes `transcript.partial` so the user sees speech while it
  // is happening. The participant stream coordinator owns speaker hand-off: a
  // second speaker's draft stays buffered until the first speaker finalizes, and
  // Output follows that same turn order.
  const sourceTokens = useMemo(
    () => {
      const filtered = displayTokens.filter((token) => {
        return isTwoWayMode && token.language
          ? token.language === sourceLangForSplit
          : token.translation_status !== 'translation'
      })
      return nativeMode
        ? sortBySpeechStart(filtered, (token) => token.start_ms)
        : filtered
    },
    [displayTokens, isTwoWayMode, nativeMode, sourceLangForSplit]
  )
  const targetTokens = useMemo(
    () =>
      displayTokens.filter((token) =>
        isTwoWayMode && token.language
          ? token.language !== sourceLangForSplit
          : token.translation_status === 'translation'
      ),
    [displayTokens, isTwoWayMode, sourceLangForSplit]
  )
  // Azure/gpt-realtime-whisper's translation only ever arrives via
  // `translation.batch` (`recording.batchTokens`), which never feeds
  // `displayTokens` above — without surfacing it separately here their Output
  // pane would stay empty. Every other provider already carries its
  // translation in `finalTokens` above (revealed there instead); routing
  // `batchTokens` for them too would show the same content twice, since
  // Soniox/google_live_translate both also emit `translation.batch` as a
  // redundant second stream alongside their `finalTokens`.
  const targetBatchTokens = useMemo(
    () =>
      isBatchOnlyTranslationProvider
        ? (nativeMode ? (nativeTranscription.batchTokens ?? []) : recording.batchTokens).filter(
            (token) =>
              isTwoWayMode && token.language
                ? token.language !== sourceLangForSplit
                : token.translation_status === 'translation'
          )
        : [],
    [
      isBatchOnlyTranslationProvider,
      isTwoWayMode,
      nativeMode,
      nativeTranscription.batchTokens,
      recording.batchTokens,
      sourceLangForSplit,
    ]
  )
  // Sorted the same way Input is, and for the same reason: each participant has
  // their own AI socket in native mode, so a translation that started earlier can
  // arrive after a later one from somebody else. Only Input was given this when
  // the order was first reported (`preserve native input speech order`), which
  // left the two columns disagreeing about what came first.
  //
  // Applied to the combined list rather than `targetTokens` alone because a
  // batch-only provider carries its whole translation in `targetBatchTokens` —
  // sorting just the other half would leave those meetings exactly as they were.
  const combinedTargetTokens = useMemo(() => {
    const combined = [...targetTokens, ...targetBatchTokens]
    return nativeMode ? sortBySpeechStart(combined, (token) => token.start_ms) : combined
  }, [nativeMode, targetTokens, targetBatchTokens])

  // Sorted, not just merged: `upsertTurn` appends in arrival order, and in
  // native mode each participant has their own AI socket, so a turn that
  // started earlier can land after a later one from somebody else. Sorting here
  // rather than in `BilingualTimeline` fixes the saved translation too —
  // `buildTranslationSegmentsFromTurns` reads this same list.
  const liveTurns = useMemo(
    () =>
      sortBySpeechStart(
        nativeMode
          ? (nativeTranscription.liveTurns ?? [])
          : mergeLiveTranscriptTurns(archivedLiveTurns, recording.liveTurns ?? []),
        (turn) => turn.startMs
      ),
    [archivedLiveTurns, nativeMode, nativeTranscription.liveTurns, recording.liveTurns]
  )
  // Mirror the merged live turns into a ref so `prepareSave` (which runs after
  // a state flush, reading refs) can reconstruct saved translation segments
  // from the batch turns for batch-only providers.
  const liveTurnsRef = useRef<LiveTranscriptTurn[]>([])
  liveTurnsRef.current = liveTurns

  /**
   * The room's cards and this socket's, as one list.
   *
   * Folded with `upsertAssistCard` rather than concatenated: a reader who was present for part of
   * the room holds some of the same `cardId`s in both halves, and the live copy is the later one.
   */
  /** Cards whose question has gone back out, and whose refusal is now stale. */
  const [retriedCardIds, setRetriedCardIds] = useState<string[]>([])
  const assistCards = useMemo(
    () => {
      const all = (recording.assistCards ?? []).reduce(upsertAssistCard, archivedAssistCards)
      // A question asked again is answered by a card of its own, and the
      // server mints a new `cardId` for it. Leaving the refusal in place would
      // show the same question twice, once turned down and once answered,
      // which reads as having asked twice.
      return retriedCardIds.length === 0
        ? all
        : all.filter((card) => !retriedCardIds.includes(card.cardId))
    },
    [archivedAssistCards, recording.assistCards, retriedCardIds]
  )

  // `prepareSave` runs from a callback that closed over an earlier render, so
  // it cannot read the memo above — the same reason the memo and the tokens
  // beside it each keep one.
  const assistCardsRef = useRef(assistCards)
  assistCardsRef.current = assistCards

  /**
   * Ask again the questions that were turned down for something now in place.
   *
   * A reader who asks about a ticket before connecting Backlog gets a refusal,
   * goes and connects, and used to have to type the question out a second time
   * to find out it would have worked.
   *
   * Sending stops at the first frame the socket refuses rather than carrying
   * on: a session that is not ready will refuse all of them, and the cards are
   * only forgotten once their question has actually gone out — so a refusal
   * here leaves the reader exactly where they were.
   *
   * @param prerequisites What is in place now, which decides which refusals
   *   are worth repeating.
   */
  const retryRefusedQuestions = useCallback(
    (prerequisites: BacklogPrerequisites) => {
      if (!assistOn) return
      const cleared = refusalsClearedBy(assistCardsRef.current, prerequisites)
      if (cleared.length === 0) return
      const asked: string[] = []
      for (const card of cleared) {
        // By id where the question came from a suggestion: the server keeps
        // every `suggestionId` for the meeting, and re-sending text would cost
        // a second classification pass that could sort it onto another route.
        const suggestionId = suggestionIdForQuestion(assistCardsRef.current, card.question)
        const ask = suggestionId ? { suggestionId } : { text: card.question }
        if (!sendAssistAsk(ask)) break
        asked.push(card.cardId)
      }
      if (asked.length > 0) setRetriedCardIds((current) => [...current, ...asked])
    },
    [assistOn, sendAssistAsk]
  )
  retryRefusedQuestionsRef.current = retryRefusedQuestions

  // Deliberately the live count, not the merged one: history a late joiner is about to be shown is
  // not unread mail, and badging it would announce a backlog they never missed. The transcript
  // replay beside it notifies nobody for the same reason.
  // A question the AI has not answered yet does count, though, which it did not use to: with the
  // panel shut that is the only thing the badge ever has to report (see `countCopilotItems`).
  const assistCardCount = countCopilotItems(recording.assistCards ?? [])

  useEffect(() => {
    if (!copilotAssistEnabled || isCopilotOpen) {
      assistCardCountRef.current = assistCardCount
      return
    }
    const previous = assistCardCountRef.current
    if (assistCardCount === 0 && previous > 0) {
      markCopilotOpened()
    } else if (assistCardCount > previous) {
      bumpUnread(assistCardCount - previous)
    }
    assistCardCountRef.current = assistCardCount
  }, [assistCardCount, bumpUnread, copilotAssistEnabled, isCopilotOpen, markCopilotOpened])

  const memoTokens = useMemo(
    () =>
      nativeMode
        ? nativeTranscription.tokens.filter((token) => token.is_final)
        : [...archivedTokens, ...recording.finalTokens],
    [archivedTokens, nativeMode, nativeTranscription.tokens, recording.finalTokens]
  )

  const liveMemo = useLiveMemoInsights({
    tokens: memoTokens,
    // In native mode the summary is the MEETING's, computed once across every participant socket
    // and pushed down each of them (see summary_hub on the AI service); `recording` has no socket
    // of its own there, so its status/memo/error would always be idle-and-empty.
    memoStatus: nativeMode ? nativeTranscription.memoStatus : recording.memoStatus,
    // Merge (not just fall back to) the last archived snapshot so the panel
    // keeps prior main_content/key_points/actions across a recording restart
    // (`recording.start()` resets `memo` to null) even once the new
    // post-restart session's first `summarize.result` arrives — a plain `??`
    // fallback would otherwise get fully replaced by the new (restart-only)
    // snapshot the instant it lands.
    memo: mergeArchivedMemo(archivedMemo, nativeMode ? nativeTranscription.memo : recording.memo),
    memoError: nativeMode ? nativeTranscription.memoError : recording.memoError,
    // `enabled` is bound to meeting lifecycle only — toggling it to false will
    // hard-reset accumulated memo/sentTranscript. The live-summary cadence is
    // now server-driven via `summarize.*` push events on the STT socket, so
    // there is no client-side pause/resume scheduling concern anymore.
    enabled: Boolean(currentMeeting),
  })

  /**
   * Replaces the server-owned Summary with the full merged narrative visible
   * to the user. Once sent, the live socket owns the archived paragraphs too,
   * so both archived representations must stop contributing them.
   *
   * @param paragraphs Complete edited Summary currently shown in the panel.
   * @returns `false` when no active socket accepted the command.
   */
  const handleMainContentSave = useCallback(
    (paragraphs: string[]): boolean => {
      if (!recording.sendMainContentUpdate(paragraphs)) return false

      setArchivedMemo((current) => (current ? { ...current, main_content: [] } : current))
      archivedMemoRef.current = archivedMemoRef.current
        ? { ...archivedMemoRef.current, main_content: [] }
        : archivedMemoRef.current
      return true
    },
    [recording]
  )

  useEffect(() => {
    if (recording.mainContentUpdateStatus !== 'error') return
    showSaveToast({
      kind: 'error',
      message: recording.mainContentUpdateError ?? t.mainContentSaveFailed,
    })
  }, [
    recording.mainContentUpdateError,
    recording.mainContentUpdateStatus,
    showSaveToast,
    t.mainContentSaveFailed,
  ])

  /** Reports an unacknowledged Summary edit without leaving the editor pending forever. */
  const handleMainContentSaveTimeout = useCallback(() => {
    showSaveToast({ kind: 'error', message: t.mainContentSaveTimeout })
  }, [showSaveToast, t.mainContentSaveTimeout])

  const recordingErrorMsg = nativeMode
    ? (nativeTranscription.error?.message ?? nativeTranscription.session?.failureMessage ?? null)
    : (recording.error?.message ?? null)
  const isPendingAutoStart =
    currentMeetingId !== null && settings.pendingStartMeetingId === currentMeetingId
  const isTransientAbort = recordingErrorMsg === 'Recording aborted' && isPendingAutoStart
  // The user closing the tab they were sharing is a deliberate act, not a
  // fault, and the session recovers from it — so it gets its own notice rather
  // than the red fault banner, which read as "the app broke" and (being raw
  // `Error.message`) was the one string on this screen that never localized.
  const isCaptureLost = !nativeMode && isCaptureLostError(recording.error)
  const showRecordingError =
    recordingErrorMsg !== null &&
    recordingErrorMsg !== dismissedError &&
    !isTransientAbort &&
    !isCaptureLost
  const captureLostNotice = !isCaptureLost
    ? null
    : recording.state === 'draining'
      ? { title: t.captureLostDrainingTitle, body: t.captureLostDrainingBody }
      : { title: t.captureLostDoneTitle, body: t.captureLostDoneBody }
  const showStartError = startError !== null
  const hasTranscript =
    nativeTranscription.tokens.length > 0 ||
    archivedTokensRef.current.length > 0 ||
    recording.finalTokens.length > 0 ||
    recording.partialTokens.length > 0
  const shouldWarnBeforeUnload =
    Boolean(currentMeeting) &&
    (nativeActive ||
      recording.isActive ||
      recording.state === 'connecting' ||
      recording.state === 'stopping' ||
      hasTranscript ||
      feedbackOpen ||
      isEndingMeeting ||
      isSavingMeeting)

  useEffect(() => {
    if (!shouldWarnBeforeUnload) return

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      event.returnValue = ''
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [shouldWarnBeforeUnload])

  /**
   * Intercept the browser Back button (and any other history pop) while a
   * recording is in flight. `<BrowserRouter>` doesn't support `useBlocker`,
   * so we push a sentinel history entry and re-push it on cancel. Confirm
   * uses the same warning copy as the reload guard so users see one rule.
   */
  useEffect(() => {
    if (!shouldWarnBeforeUnload) return

    window.history.pushState(null, '', window.location.href)

    const handlePopState = () => {
      const confirmed = window.confirm(t.leaveWhileRecordingPrompt)
      if (confirmed) {
        // Let the user actually navigate back. The sentinel we just popped is
        // gone, so one more back() pops the previous real entry.
        window.removeEventListener('popstate', handlePopState)
        window.history.back()
        return
      }
      // Re-arm: push a fresh sentinel so the next Back press is intercepted again.
      window.history.pushState(null, '', window.location.href)
    }

    window.addEventListener('popstate', handlePopState)
    return () => window.removeEventListener('popstate', handlePopState)
  }, [shouldWarnBeforeUnload, t.leaveWhileRecordingPrompt])

  /**
   * Persist metadata + navigate to the detail page immediately, then close the
   * recording socket, ask the backend for the minutes, and upload audio — all
   * in the background. The metadata POST is awaited so we can route to the
   * real backend id quickly.
   *
   * Since BRAI-412 only the *request* for minutes happens here; the generation
   * itself belongs to the server, so it now survives this tab being closed
   * mid-way. The detail page shows its own "generating..." skeleton while
   * `insightsStatus` is `PENDING`.
   */
  const persistMeetingAndNavigate = useCallback(
    async (
      prepared: CompletedMeetingSave,
      feedback: { rating: number; comment: string }
    ): Promise<void> => {
      const {
        meeting,
        durationSeconds,
        transcript,
        translationSegments,
        audio,
        liveMemo,
        assistCards,
      } = prepared
      const pendingClientId = meeting.id

      // Native meetings send the captured snapshot too. MeetingsService stores
      // it first as a lossless fallback, then replaces the source transcript
      // with the reconciled backend projection when native segments are ready.
      // Translation segments still come from this client pipeline, so omitting
      // either field can leave Meeting Detail empty after End Meeting.
      const savedMeeting = await completeMeetingMetadata(meeting.id, {
        durationSeconds,
        transcript,
        translationSegments,
        title: meeting.title,
        // Feedback is optional now (skippable) — the backend rejects
        // `feedbackRating: 0` (@Min(1)), so omit both fields entirely rather
        // than send an empty rating/comment.
        feedbackRating: feedback.rating > 0 ? feedback.rating : undefined,
        feedbackComment: feedback.comment || undefined,
        realtimeContext: savedContextValueRef.current,
        liveMemo,
        // Empty stays empty rather than becoming null: the meeting ran, the
        // Copilot raised nothing, and that is a different fact from a meeting
        // that ended before any of this was stored.
        assistCards,
      })

      addPendingSavedMeeting({
        clientId: pendingClientId,
        title: savedMeeting.title ?? meeting.title,
        sourceLang: meeting.sourceLang,
        targetLang: meeting.targetLang,
        durationSeconds,
        startedAt: Date.now(),
      })
      deleteMeeting(meeting.id)
      navigate(`/meetings/${savedMeeting.id}`, { state: { from: '/home' } })

      // Held for the whole background block below. It runs after `navigate`,
      // so the tab already looks finished while the recording is still going
      // up — closing it here is the one way an otherwise healthy meeting
      // loses its audio outright.
      const releaseUnloadGuard = guardPendingUpload()

      void (async () => {
        const results = await Promise.allSettled([
          uploadMeetingAudioInBackground(savedMeeting.id, audio),
          // Closes the socket (flushing whatever the proxy still owes, cost
          // included) and then hands the minutes to the server, which finishes
          // them whether or not this tab is still open.
          recordingStopRef.current().then(() => requestMeetingMinutes(savedMeeting.id)),
        ])

        // `cost.summary` typically arrives during the `stop` sequence above,
        // i.e. after this component has already navigated away and unmounted
        // — so it can't be read from the metadata payload sent earlier.
        // Best-effort: persist it now if it showed up by the time the
        // background work above settled.
        // Native mode bills PER PARTICIPANT SOCKET, so the meeting's figure is their sum — the
        // single-socket ref would only ever hold one participant's share (and, without a socket of
        // its own there, actually holds nothing).
        const costSummary = nativeMode
          ? nativeReadCostSummaryRef.current()
          : recordingCostSummaryRef.current.current
        if (costSummary) {
          await updateMeeting(savedMeeting.id, {
            currency: costSummary.currency,
            totalCostUsd: costSummary.totalCostUsd,
          }).catch((error) => {
            console.warn('Meeting cost summary save failed', error)
          })
        }

        removePendingSavedMeeting(pendingClientId)

        const audioFailed = results[0].status === 'rejected'
        // Only the *request* can fail here; the generation itself is the
        // server's problem from this point on, and the detail page reads its
        // outcome from `insightsStatus`.
        const minutesFailed = results[1].status === 'rejected'

        if (audioFailed && minutesFailed) {
          console.warn('Meeting background save failed', results)
          showSaveToast({ kind: 'error', message: t.meetingSaveFailedToast })
        } else if (audioFailed) {
          console.warn('Meeting audio upload failed', results[0])
          showSaveToast({ kind: 'error', message: t.failedUploadMeetingAudio })
        } else if (minutesFailed) {
          console.warn('Meeting minutes request failed', results[1])
        } else {
          showSaveToast({ kind: 'success', message: t.meetingSavedToast })
        }
      })()
        .catch((error: unknown) => {
        // The row on Home is the only thing telling the reader this save is
        // still running, and nothing above is guaranteed to reach the line
        // that clears it. A throw used to leave the row behind AND go out as
        // an unhandled rejection, so nobody learned either fact.
          console.warn('Meeting background save threw', error)
          removePendingSavedMeeting(pendingClientId)
        })
        .finally(releaseUnloadGuard)
    },
    [
      addPendingSavedMeeting,
      deleteMeeting,
      navigate,
      removePendingSavedMeeting,
      showSaveToast,
      nativeMode,
      t.meetingSaveFailedToast,
      t.meetingSavedToast,
      t.failedUploadMeetingAudio,
    ]
  )

  /**
   * Awaits transcript/audio preparation, then persists metadata and navigates
   * to the freshly saved meeting's detail page. Meeting-minutes generation
   * (live socket first, REST as a last-resort fallback) runs in the
   * background inside `persistMeetingAndNavigate`, AFTER navigation, so
   * Submit stays fast regardless of how long AI processing takes — the
   * socket only needed to survive until Submit was clicked (it was muted,
   * not torn down, when "End Meeting" was clicked), not for the whole
   * request/response round trip. The feedback modal stays open with a
   * spinner during the metadata POST so the user can retry inline on
   * failure.
   */
  const saveCompletedMeeting = (feedback: { rating: number; comment: string }) => {
    if (isSavingMeeting) return

    setIsSavingMeeting(true)
    setSaveError(null)

    void (async () => {
      try {
        const preparation =
          preparedSavePromiseRef.current ?? deferredPrepareSaveRef.current?.() ?? null
        const prepared = preparedSaveRef.current ?? (await preparation)
        if (!prepared) {
          throw new Error(t.failedSaveMeeting)
        }
        await persistMeetingAndNavigate(prepared, feedback)
        setFeedbackOpen(false)
      } catch (error) {
        console.warn('Meeting save failed', error)
        setSaveError(error instanceof Error ? error.message : t.failedSaveMeeting)
      } finally {
        setIsSavingMeeting(false)
      }
    })()
  }

  /**
   * Stops capture and prepares transcript/audio in the background. Manual
   * endings open feedback immediately; provider-driven completion skips it
   * and saves straight through to Meeting Detail.
   */
  const handleEndMeeting = async (skipFeedback = false) => {
    if (!currentMeeting || endMeetingRequestedRef.current || isEndingMeeting) return

    endMeetingRequestedRef.current = true
    // On the way in, not on the way out: ending runs a long tail of saving and
    // uploading, and whether somebody finished a meeting is not a question
    // about whether the upload worked (BRAI-677).
    logActivity('meeting.ended', {
      meetingId: currentMeeting.id,
      projectKey: currentMeeting.backlogProjectKey ?? null,
    })
    // Which panels the reader actually worked with, read off the layout here
    // rather than counted from clicks on the fold controls — see
    // `openPanelActivities` for why that distinction decides the answer.
    for (const panelActivity of openPanelActivities({
      sourceOpen,
      targetOpen,
      batchOpen,
      memoOpen,
      keyPointsOpen,
    })) {
      logActivity(panelActivity, {
        meetingId: currentMeeting.id,
        projectKey: currentMeeting.backlogProjectKey ?? null,
      })
    }
    setDurationWarningStep('closed')
    durationWarningActionLockRef.current = false
    setStartError(null)
    setSaveError(null)
    preparedSaveRef.current = null
    preparedSavePromiseRef.current = null
    deferredPrepareSaveRef.current = null

    const durationSeconds = Math.max(elapsedSecondsRef.current, elapsedSeconds)
    const startedAt = meetingStartedAtRef.current ?? Date.now() - durationSeconds * 1000
    const titleForSave = resolveMeetingTitleForSave(
      currentMeeting.title,
      sharedRoomPresentation?.meetingTitle
    )
    endingMeetingTitleRef.current = titleForSave
    clearSharedRoomRejoinSnapshot(currentMeeting.id)
    sharedRoom.clearDismissedMatch()
    const meetingSnapshot = {
      id: currentMeeting.id,
      title: titleForSave,
      sourceLang: currentMeeting.sourceLang,
      targetLang: currentMeeting.targetLang,
    }

    setCompletedDurationSeconds(durationSeconds)
    setFeedbackOpen(!skipFeedback)

    // Both capture modes mute/pause the source (reusing the exact mechanism
    // already proven for the Pause button) instead of stopping capture
    // outright while the feedback modal is open. Native pause deliberately
    // keeps the same gateway socket, bot seat, participant AI streams, audio
    // archive and session identity alive, so Cancel can resume losslessly.
    // Provider-driven completion is already stopped server-side and needs no
    // local pause.
    if (nativeMode) {
      if (!nativeSessionCompleted) nativeTranscription.pause()
    } else {
      pauseScreenCapture()
      recording.pause()
    }

    const prepareSave = async (): Promise<CompletedMeetingSave> => {
      setIsEndingMeeting(true)
      // A manual native end is only destructive after Submit/Skip. Failure is
      // best-effort: saving the transcript/audio already captured is still more
      // important than receiving the gateway's leave acknowledgement.
      if (nativeMode && !nativeSessionCompleted) {
        try {
          await nativeTranscription.stop()
        } catch (error) {
          console.warn('Failed to stop the native transcription session', error)
        }
      }
      // Native mode has no tab MediaStream to record; its audio is the PCM the
      // gateway streamed to this browser, re-encoded by `NativeMeetingAudioArchive`.
      const archivedAudio = normalizeArchivedAudio(
        nativeMode
          ? await nativeTranscription.stopAudioArchive()
          : await archiveRecorderRef.current.stop()
      )
      await waitForStateFlush()

      // Native tokens arrive already bucketed into speaker turns (`groupLiveTokensBySpeakerTurn`),
      // which is right for the live panes — it stops two people talking at once from cutting each
      // other's sentences into fragments — but the saved segments are built in ARRAY ORDER, so they
      // inherited that grouping and an interleaved A/B/A conversation was stored as A/A/B
      // (BRAI-368). Sorting the tokens back into speech order here fixes the saved source AND
      // translation segments, which are both derived from this one list. Browser tokens never go
      // through the grouping, so they are left alone.
      const finalTokens = nativeMode
        ? sortBySpeechStart(nativeFinalTokensRef.current, (token) => token.start_ms)
        : [...archivedTokensRef.current, ...recordingFinalTokensRef.current]
      const transcript = buildSourceSegments(finalTokens, startedAt)
      // Enhanced mode's batch turns are preferred, and the inline stream is the
      // fallback — the order used to be the other way round (BRAI-642).
      //
      // Both describe the same speech. The inline translation (Soniox,
      // google_live_translate, arriving on `finalTokens` via `translation.final`)
      // is what the reader saw word by word while the meeting ran; the batch
      // translation is the same passage after the service has seen the whole of
      // it, which is the copy Enhanced mode shows and the better one to keep.
      // Saving the realtime copy meant the minutes screen could only ever show
      // the rougher of the two, and the better one was thrown away when the
      // socket closed.
      //
      // Batch-only providers (azure/azure_speech_recognizer, gpt-realtime-whisper)
      // emit no inline translation at all, so they already took this path. The
      // choice is driven by what actually arrived rather than by the provider,
      // so a meeting whose batch stream produced nothing still keeps its inline
      // translation instead of losing the column.
      const translationSegments = chooseSavedTranslationSegments(
        buildTranslationSegmentsFromTurns(liveTurnsRef.current, startedAt),
        buildTranslationSegments(finalTokens, startedAt),
      )
      const finalLiveMemo = mergeArchivedMemo(archivedMemoRef.current, recordingMemoRef.current)
      // A `pending` answer is a question the closing socket will never answer,
      // so it is dropped rather than stored spinning for good.
      const finalAssistCards = assistCardsRef.current.filter(
        (card) => !isPendingAnswerCard(card)
      )

      const prepared: CompletedMeetingSave = {
        meeting: meetingSnapshot,
        durationSeconds,
        transcript,
        translationSegments,
        audio: archivedAudio,
        liveMemo: finalLiveMemo,
        assistCards: finalAssistCards,
      }

      preparedSaveRef.current = prepared
      return prepared
    }

    const schedulePrepareSave = (): Promise<CompletedMeetingSave> => {
      const existing = preparedSavePromiseRef.current
      if (existing) return existing

      deferredPrepareSaveRef.current = null
      const preparation = runInBackground(prepareSave)
        .catch((error) => {
          setSaveError(error instanceof Error ? error.message : t.failedSaveMeeting)
          throw error
        })
        .finally(() => {
          endMeetingRequestedRef.current = false
          setIsEndingMeeting(false)
        })
      preparedSavePromiseRef.current = preparation
      void preparation.catch(() => {
        /* The feedback modal shows the preparation error if stopping capture fails. */
      })
      return preparation
    }

    deferredPrepareSaveRef.current = schedulePrepareSave

    // Browser capture keeps its existing behavior: preparation begins as soon
    // as End is clicked. Native manual endings wait for explicit confirmation;
    // provider completion (`skipFeedback`) still finalizes immediately.
    if (!nativeMode || skipFeedback) schedulePrepareSave()

    if (skipFeedback) {
      saveCompletedMeeting({ rating: 0, comment: '' })
    }
  }

  handleEndMeetingRef.current = () => {
    void handleEndMeeting(true)
  }

  useEffect(() => {
    if (!nativeSessionCompleted) return
    handleEndMeetingRef.current()
  }, [nativeSessionCompleted])

  /**
   * Fully undoes "End Meeting": resumes capture/streaming and closes the
   * feedback modal without saving. Both browser and native modes resume the
   * exact paused session losslessly; no tokens, socket, audio archive, bot seat
   * or native session identity are discarded while feedback is pending.
   */
  const handleCancelFeedback = async () => {
    if (isSavingMeeting) return

    setSaveError(null)
    preparedSaveRef.current = null
    preparedSavePromiseRef.current = null
    deferredPrepareSaveRef.current = null

    try {
      if (nativeMode) {
        nativeTranscription.resume()
      } else {
        recording.resume()
        resumeScreenCapture()
      }
    } finally {
      setFeedbackOpen(false)
      setIsEndingMeeting(false)
      endMeetingRequestedRef.current = false
      setCompletedDurationSeconds(null)
    }
  }

  const isRecordingForDurationWarning = nativeMode ? nativeActive : recording.isActive
  const isPausedForDurationWarning = nativeMode
    ? nativeTranscription.isPaused
    : recording.isPaused

  // Auto-pause and show check-in when header elapsed crosses 90m, then every +30m after Yes.
  useEffect(() => {
    if (
      !shouldTriggerDurationWarning({
        elapsedSeconds,
        nextWarningAtSeconds,
        isRecording: isRecordingForDurationWarning,
        isPaused: isPausedForDurationWarning,
        warningOpen: durationWarningStep !== 'closed',
        isEnding: isEndingMeeting,
        feedbackOpen,
      })
    ) {
      return
    }

    setDurationWarningStep('checkin')

    if (nativeMode) {
      nativeTranscription.pause()
    } else {
      recording.pause()
      pauseScreenCapture()
    }
  }, [
    durationWarningStep,
    elapsedSeconds,
    feedbackOpen,
    isEndingMeeting,
    isPausedForDurationWarning,
    isRecordingForDurationWarning,
    nativeMode,
    nativeTranscription,
    nextWarningAtSeconds,
    pauseScreenCapture,
    recording,
  ])

  /** Yes, continue — close check-in, advance next threshold, resume capture. */
  const handleDurationWarningYesContinue = useCallback(() => {
    if (durationWarningStep !== 'checkin' || durationWarningActionLockRef.current) return
    durationWarningActionLockRef.current = true

    setDurationWarningStep('closed')
    setNextWarningAtSeconds((current) =>
      advanceDurationWarningAt(current, elapsedSecondsRef.current)
    )

    if (nativeMode) {
      nativeTranscription.resume()
    } else {
      recording.resume()
      resumeScreenCapture()
    }

    durationWarningActionLockRef.current = false
  }, [durationWarningStep, nativeMode, nativeTranscription, recording, resumeScreenCapture])

  /** No on check-in — show end confirmation (stay paused). */
  const handleDurationWarningNoFromCheckIn = useCallback(() => {
    if (durationWarningStep !== 'checkin') return
    setDurationWarningStep('confirm')
  }, [durationWarningStep])

  /** No, go back — return to check-in (stay paused). */
  const handleDurationWarningNoGoBack = useCallback(() => {
    if (durationWarningStep !== 'confirm') return
    setDurationWarningStep('checkin')
  }, [durationWarningStep])

  /** Yes, stop — close dialogs and run the normal end-meeting flow. */
  const handleDurationWarningYesStop = useCallback(() => {
    if (durationWarningStep !== 'confirm' || durationWarningActionLockRef.current) return
    durationWarningActionLockRef.current = true
    setDurationWarningStep('closed')
    durationWarningActionLockRef.current = false
    void handleEndMeeting()
  }, [durationWarningStep, handleEndMeeting])

  if (!currentMeeting) {
    return (
      <div className="flex min-h-full flex-1 items-center justify-center">
        <p className="text-sm text-[var(--text-tertiary)]">{t.selectMeeting}</p>
      </div>
    )
  }

  const isTranslating = shouldKeepSessionStatus
  // One status for both capture modes, spoken in the browser flow's own state vocabulary. A Teams
  // meeting used to print the backend's session enum here (`CONNECTED`, `WAITING_CONSENT`,
  // `FAILED`, …) — untranslated, and in words share-tab meetings never use — so the same meeting
  // read differently depending on how it was captured (BRAI-360). `draining` and `interrupted`
  // stay browser-only and `waitingConsent` native-only, because that is where each actually
  // occurs; nothing is folded together just to make the two lists match.
  const sessionState: MeetingSessionState = nativeMode
    ? resolveNativeSessionState({
        sessionStatus: nativeTranscription.session?.status,
        isPaused: nativeTranscription.isPaused,
        failureMessage: recordingErrorMsg,
      })
    : recording.state
  const statusLabel = getMeetingSessionStatusLabel(
    sessionState,
    {
      idle: t.readyToTranslate,
      connecting: t.statusConnecting,
      waitingConsent: t.statusWaitingConsent,
      recording: t.aiTranslating,
      paused: t.aiLivePaused,
      stopping: t.statusStopping,
      draining: t.finishingTranslations,
      interrupted: t.statusInterrupted,
      stopped: t.statusEnded,
      error: t.statusFailed,
    },
    recordingErrorMsg
  )
  const statusTone = meetingSessionStatusTone(sessionState)
  const canStartMeeting =
    // A start needs the network: without it the socket cannot open, and letting
    // the click through is what made a network blip look like a brand new
    // meeting (BRAI-366).
    isOnline &&
    !nativeActive &&
    !recording.isActive &&
    recording.state !== 'connecting' &&
    recording.state !== 'stopping' &&
    // Starting mid-drain would open a second socket while the first is still
    // collecting the previous session's translations, and the new one would win
    // the pane.
    recording.state !== 'draining' &&
    !isEndingMeeting &&
    !feedbackOpen
  /** Play resumes the interrupted session rather than opening a new one. */
  const isSessionInterrupted = !nativeMode && recording.state === 'interrupted'
  /**
   * Whether Start can be pressed — and, because they are the same answer,
   * whether it bobs to be noticed (BRAI-685).
   *
   * One expression for both so the two cannot drift apart. Beckoning at a
   * disabled control is the contradiction BRAI-691 was about, seen from the
   * other side: a screen inviting a click that would do nothing.
   */
  const startDisabled = !canStartMeeting || isStartingMeeting
  /**
   * Copilot bobs on the same terms as Start: whenever it is on screen with
   * something to press it for.
   *
   * Not while its own panel is open. The button is a toggle, already pressed,
   * and bobbing it there would be inviting the reader to close what they have
   * just opened — the same rule as Start's, which does not bob while it cannot
   * be pressed.
   *
   * Neither control remembers anything: the burst is three bobs and about two
   * seconds each time the control appears, not motion that keeps going, so
   * there is nothing here that needed a flag to switch off.
   */
  const nudgeCopilot = !isCopilotOpen
  const canPauseMeeting = nativeMode ? nativeActive : recording.isActive
  /**
   * The Copilot hint waits for the meeting to be running.
   *
   * Two bubbles over one row would be two things to read at once, and Start is
   * the one that has to happen first — nothing is recorded until it does. By
   * the time this appears that question is settled, and Copilot has a meeting
   * to answer questions about.
   */
  const showCopilotHint =
    !isCopilotOpen &&
    // News outranks the introduction and outlives it: something has arrived
    // that the reader has not seen, and that is worth saying whether or not
    // they have been told what Copilot is for.
    (copilotUi.unreadCount > 0 || (canPauseMeeting && !copilotHintSpent))
  const isMeetingPaused = nativeMode ? nativeTranscription.isPaused : recording.isPaused
  const toggleMeetingPause = () => {
    // Screen capture only ever runs in browser mode — a native meeting has no
    // captured tab of its own — so it is paired with `recording` alone.
    if (nativeMode) {
      if (isMeetingPaused) nativeTranscription.resume()
      else nativeTranscription.pause()
      return
    }
    if (isMeetingPaused) {
      recording.resume()
      resumeScreenCapture()
    } else {
      recording.pause()
      pauseScreenCapture()
    }
  }
  const canEndMeeting =
    (nativeActive || recording.isActive || recording.state === 'error' || hasTranscript) &&
    !isEndingMeeting &&
    !feedbackOpen
  // The realtime-context control needs an actively open WS session — matches
  // the states in which `recording` accepts other live commands (mute, pause).
  const isContextSessionReady = recording.state === 'recording' || recording.state === 'paused'
  /**
   * The name of the meeting on screen: the room's, once there is a room.
   *
   * A machine merged into somebody else's room keeps its own row's title —
   * often a `Meeting#N` nobody chose — while the room itself has a name BrSE
   * sends as `peerMeetingTitle`. Inside a room the header uses the Figma form
   * `Shared room · {title}`; outside, the meeting's own title is all there is.
   */
  const roomLabel = sharedRoomPresentation?.meetingTitle ?? currentMeeting.title
  const headerTitle = inSharedRoom
    ? fmt(t.sharedRoomHeaderJoined, { title: roomLabel })
    : currentMeeting.title
  return (
    <div className="flex h-full flex-col bg-white text-slate-950 dark:bg-[var(--bg-secondary)] dark:text-[var(--text-primary)]">
      <header className="flex min-h-0 shrink-0 flex-nowrap items-center gap-2 border-b border-blue-100 bg-white px-3 py-2 dark:border-[var(--border-color)] dark:bg-[var(--bg-secondary)] sm:min-h-[72px] sm:flex-wrap sm:gap-3 sm:px-6 sm:py-3">
        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 flex-nowrap items-center gap-2 sm:gap-3">
            <h1
              title={headerTitle}
              className="min-w-0 truncate text-[11px] font-bold text-slate-950 dark:text-[var(--text-primary)] sm:text-sm"
            >
              {headerTitle}
            </h1>
            <span
              data-testid="meeting-language-badge"
              className="inline-flex max-w-full shrink-0 items-center gap-1 rounded-full bg-slate-100 px-1.5 py-0.5 text-[11px] font-semibold text-slate-700 dark:bg-[var(--bg-tertiary)] dark:text-[var(--text-secondary)] sm:gap-2 sm:px-3 sm:py-1 sm:text-xs"
            >
              <span className="truncate">
                {currentMeeting.sourceLang
                  ? formatLanguageCode(currentMeeting.sourceLang)
                  : t.autoDetectBadge}
              </span>
              <span aria-hidden="true">→</span>
              <select
                value={currentMeeting.targetLang}
                onChange={(event) => handleTargetLangChange(event.target.value)}
                disabled={isChangingTargetLang}
                className="max-w-[40vw] cursor-pointer truncate bg-transparent text-[11px] font-semibold text-slate-700 outline-none disabled:opacity-50 dark:text-[var(--text-secondary)] sm:max-w-none sm:text-xs"
                aria-label={t.translationLanguageAria}
              >
                {LANGUAGES.map((language) => (
                  <option key={language.code} value={language.code}>
                    {getLanguageDisplayName(language.code, t)}
                  </option>
                ))}
              </select>
            </span>
            <SharedRoomHeaderStrip
              variant={sharedRoomUi.headerVariant}
              roomTitle={sharedRoomPresentation?.meetingTitle ?? null}
              members={sharedRoomPresentation?.participants ?? []}
              canLeave={canLeaveSharedRoom}
              leaving={leavingSharedRoom}
              t={t}
              locale={locale}
              onJoin={sharedRoomUi.openDetectionModal}
              onLeave={sharedRoomUi.openLeaveConfirm}
            />
          </div>
        </div>

        {/* Status and elapsed time stay on one row with the title on mobile so the
            header never wraps into multiple lines on narrow viewports (BRAI-360). */}
        <div className="ml-auto flex shrink-0 flex-row flex-wrap items-center justify-end gap-1.5 sm:gap-3">
          {sharedRoomUi.headerVariant === 'inSharedRoom' ? (
            <SharedRoomInRoomControls
              members={sharedRoomPresentation?.participants ?? []}
              waitingMembers={liveInviteWaitingMembers}
              canLeave={canLeaveSharedRoom}
              canInvite={canInviteMembers}
              leaving={leavingSharedRoom}
              t={t}
              locale={locale}
              onLeave={sharedRoomUi.openLeaveConfirm}
              onInvite={() => setInviteMembersOpen(true)}
              onMembersOpen={() => {
                void reloadLiveInviteRoster()
              }}
            />
          ) : canInviteMembers ? (
            <LiveInviteButton t={t} onClick={() => setInviteMembersOpen(true)} />
          ) : null}
          <div
            data-testid="meeting-status-pill"
            className={[
              'inline-flex max-w-[28vw] items-center gap-1 rounded-lg border px-1.5 py-0.5 text-[11px] font-semibold sm:max-w-[40vw] sm:gap-1.5 sm:rounded-xl sm:px-4 sm:py-2 sm:text-sm',
              STATUS_PILL_CLASS[statusTone],
            ].join(' ')}
          >
            <span className={`h-1.5 w-1.5 shrink-0 rounded-full sm:h-2 sm:w-2 ${STATUS_DOT_CLASS[statusTone]}`} />
            {/* An error status carries the server's message, which can be long — truncate rather
                than let it push the timer off the row, and keep the full text in the tooltip. */}
            <span className="truncate" title={statusLabel}>
              {statusLabel}
            </span>
          </div>

          <div
            data-testid="meeting-elapsed-timer"
            className={[
              'flex items-center gap-1.5 font-mono text-sm font-bold sm:gap-2 sm:text-xl',
              isTranslating ? 'text-red-700' : 'text-slate-400 dark:text-[var(--text-tertiary)]',
            ].join(' ')}
          >
            <span
              className={
                isTranslating
                  ? 'h-2 w-2 rounded-full bg-red-700'
                  : 'h-2 w-2 rounded-full bg-slate-300 dark:bg-[var(--text-tertiary)]'
              }
            />
            {formatElapsedHms(elapsedSeconds)}
          </div>
        </div>
      </header>

      {showStartError && (
        <div className="mx-3 mt-4 animate-fade-in rounded-xl border border-red-500/20 bg-red-500/10 px-3 py-2 sm:mx-6 sm:px-4 sm:py-3">
          <div className="flex items-center justify-between gap-3">
            <p className="min-w-0 text-xs font-medium text-red-500 sm:text-sm">{startError}</p>
            <button
              type="button"
              onClick={() => setStartError(null)}
              className="flex min-h-[44px] min-w-[44px] shrink-0 touch-manipulation items-center justify-center rounded-lg p-2 transition-colors hover:bg-red-500/10 sm:min-h-0 sm:min-w-0 sm:p-1"
            >
              <XMarkIcon className="h-4 w-4 text-red-400" />
            </button>
          </div>
        </div>
      )}

      {!isOnline && (
        <div
          role="status"
          aria-live="polite"
          className="m-3 animate-fade-in rounded-xl border border-amber-300 bg-amber-50 px-3 py-2 dark:border-amber-500/30 dark:bg-amber-500/10 sm:mx-6 sm:px-4 sm:py-3"
        >
          <p className="text-xs font-semibold text-amber-900 dark:text-amber-300 sm:text-sm">
            {t.offlineWarningTitle}
          </p>
          <p className="mt-1 text-xs leading-relaxed text-amber-800 dark:text-amber-200/80">
            {t.offlineWarningBody}
          </p>
        </div>
      )}

      {captureLostNotice && (
        <div
          className="m-3 animate-fade-in rounded-xl border border-yellow-500/30 bg-yellow-500/10 px-3 py-2 sm:mx-6 sm:px-4 sm:py-3"
          role="status"
          data-testid="capture-lost-notice"
        >
          <p className="text-xs font-semibold text-yellow-700 dark:text-yellow-300 sm:text-sm">
            {captureLostNotice.title}
          </p>
          <p className="mt-0.5 text-xs leading-5 text-[var(--text-secondary)]">
            {captureLostNotice.body}
          </p>
        </div>
      )}

      {showRecordingError && (
        <div className="m-3 animate-fade-in rounded-xl border border-red-500/20 bg-red-500/10 px-3 py-2 sm:mx-6 sm:px-4 sm:py-3">
          <div className="flex items-center justify-between gap-3">
            <p className="min-w-0 text-xs font-medium text-red-500 sm:text-sm">{recordingErrorMsg}</p>
            <button
              type="button"
              onClick={() => setDismissedError(recordingErrorMsg)}
              className="flex min-h-[44px] min-w-[44px] shrink-0 touch-manipulation items-center justify-center rounded-lg p-2 transition-colors hover:bg-red-500/10 sm:min-h-0 sm:min-w-0 sm:p-1"
            >
              <XMarkIcon className="h-4 w-4 text-red-400" />
            </button>
          </div>
        </div>
      )}

      <div
        className="relative flex min-h-0 flex-1 flex-col overflow-hidden"
        data-testid="meeting-main-area"
      >
        <div className="relative flex min-h-0 min-w-0 flex-1 overflow-hidden">
          {copilotAssistEnabled && !isLgUp && isCopilotOpen ? (
            <button
              type="button"
              className="fixed inset-0 z-30 bg-slate-950/30 lg:hidden"
              aria-label={t.copilotCloseAria}
              onClick={() => {
                setIsCopilotOpen(false)
                setAssist(false)
              }}
              data-testid="copilot-backdrop"
            />
          ) : null}
          <div className="flex min-h-0 min-w-0 flex-1 flex-col">
            <MeetingInsights
              sourceLang={currentMeeting.sourceLang}
              targetLang={currentMeeting.targetLang}
              sourceTokens={sourceTokens}
              targetTokens={combinedTargetTokens}
              sourceOpen={sourceOpen}
              onToggleSource={() => setSourceOpen((open) => !open)}
              targetOpen={targetOpen}
              onToggleTarget={() => setTargetOpen((open) => !open)}
              highlightNewestOutput={emphasis.outputHighlight}
              enlargeNewestTranslation={emphasis.enhancedTextBump}
              memoOpen={memoOpen}
              onToggleMemo={toggleMemo}
              keyPointsOpen={keyPointsOpen}
              onToggleKeyPoints={toggleKeyPoints}
              keyPoints={liveMemo.keyPoints}
              liveActions={liveMemo.actions}
              memoStatus={liveMemo.status}
              memoError={liveMemo.error}
              mainContent={liveMemo.memo?.main_content ?? []}
              onMainContentSave={recording.isActive ? handleMainContentSave : undefined}
              mainContentUpdateStatus={recording.mainContentUpdateStatus}
              mainContentUpdateError={recording.mainContentUpdateError}
              onMainContentSaveTimeout={handleMainContentSaveTimeout}
              liveTurns={liveTurns}
              batchOpen={batchOpen}
              onToggleBatch={toggleBatch}
            />
          </div>
          {copilotPanel.present ? (
            <CopilotSidebar
              ui={copilotUi}
              cards={assistCards}
              askError={recording.assistAskError ?? null}
              isAsking={recording.assistAskPending ?? false}
              onAsk={askCopilot}
              askDisabled={!assistOn}
              onClose={() => {
                setIsCopilotOpen(false)
                setAssist(false)
              }}
              shown={copilotPanel.shown}
              setupStrip={
                <CopilotSetupStrip
                  meetingProjectKey={meetingProjectKey}
                  enabled={copilotSetupEnabled}
                  onChooseProject={chooseCopilotProject}
                  onAccountKnown={applyBacklogAccount}
                  projectUpdateStatus={recording.projectUpdateStatus}
                  inSharedRoom={inSharedRoom}
                  roomProjectKey={roomProjectKey}
                  roomProjectMismatchNotice={roomProjectMismatchNoticeText}
                  offersBacklogSetup={offersBacklogSetup}
                  // Into the strip rather than its own row above it: the chips
                  // and the two pickers wrap as one group, and two sibling rows
                  // cannot share a line.
                  routesControl={
                    <CopilotSearchFilter
                      value={assistRoutes.routes}
                      onToggle={assistRoutes.toggle}
                      backlogAvailable={assistRoutes.backlogAvailable}
                      backlogUnavailableReason={backlogRouteReasonText}
                    />
                  }
                  modelControl={
                    <AssistModelSelect
                      selected={assistModel.selected}
                      pending={assistModel.pending}
                      isUnavailable={assistModel.unavailable}
                      onSelect={assistModel.select}
                      t={t}
                    />
                  }
                />
              }
              onRequestConnect={openBacklogConnect}
            />
          ) : null}
          {/* Outside the panel because it is the meeting that owns what the
              dialog needs — the meeting row, and the socket the project is
              applied to. It draws itself into the body regardless. */}
          {connectOpen ? (
            <BacklogConnectDialog
              onClose={() => setConnectOpen(false)}
              meetingProjectKey={meetingProjectKey}
              onChooseProject={chooseProjectFromDialog}
              onConnected={handleBacklogConnected}
            />
          ) : null}
        </div>

        {!isLgUp && !mobileFooterOpen ? (
          <ChromeRestoreStrip
            onShow={() => setMobileFooterOpen(true)}
            showLabel={t.showMeetingFooter}
            testId="restore-meeting-footer"
          />
        ) : null}

        <CollapsibleChrome open={isLgUp || mobileFooterOpen} testId="meeting-bottom-chrome">
          <div
            className="relative shrink-0 border-t border-[var(--border-color)] px-3 py-2 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:px-6 sm:py-3"
            data-testid="meeting-footer-surface"
          >
            <div className="mb-1 flex justify-end lg:hidden">
              <ChromeHideButton
                onHide={() => setMobileFooterOpen(false)}
                hideLabel={t.hideMeetingFooter}
                hideIconDirection="down"
                testId="hide-meeting-footer"
              />
            </div>
            {/* `max-w-4xl`, up from `2xl`: the labels moved inside the controls
                and now cost width. The wider cap prevents the remaining named
                actions from shrinking the transport controls.

                One `gap-2` and one `py-2` at every width, where there used to be
                three gaps and two paddings. Both are what the design measures —
                7–8px between controls, 7px above and below them — and neither
                has a reason to change with the viewport now that the labels,
                not the gaps, are what the row spends its width on. */}
            <div
              className="mx-auto flex w-full max-w-4xl flex-nowrap items-center justify-center gap-2 rounded-2xl bg-white px-3 py-1.5 dark:bg-[var(--bg-tertiary)] sm:px-5"
              data-testid="meeting-control-bar"
            >
              {/* Icon-only, like the pause beside it: the two transport
                  controls are read from their shape, and spelling them out
                  would push the named actions off a narrow bar. */}
              <button
                  type="button"
                  disabled={!recording.isActive || !settings.meetingSetup.hasMicrophone}
                  onClick={toggleMicMute}
                  className={[
                    'flex h-9 w-9 touch-manipulation items-center justify-center rounded-xl text-xs font-semibold transition-colors disabled:opacity-40',
                    isMicMuted
                      ? 'bg-red-100 text-red-600'
                      : 'bg-blue-100 text-blue-700 dark:bg-[var(--accent-light)] dark:text-[var(--accent)]',
                  ].join(' ')}
                  title={
                    settings.meetingSetup.hasMicrophone
                      ? isMicMuted
                        ? t.unmute
                        : t.mute
                      : t.noMicrophoneDetected
                  }
                >
                  {isMicMuted ? <MicOffIcon className="h-4 w-4" /> : <MicIcon className="h-4 w-4" />}
                </button>

              <div className="relative flex flex-col items-center gap-1">
                <ActionHintBubble
                  id="start-meeting-hint"
                  text={t.startMeetingHint}
                  show={!startDisabled}
                />
                {canPauseMeeting ? (
                  <button
                    type="button"
                    onClick={toggleMeetingPause}
                    disabled={durationWarningStep !== 'closed'}
                    className="flex h-9 w-9 touch-manipulation items-center justify-center rounded-xl bg-amber-50 text-amber-600 transition-colors hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-40 dark:bg-amber-500/15 dark:text-amber-300 dark:hover:bg-amber-500/25"
                    title={isMeetingPaused ? t.resume : t.pause}
                  >
                    {isMeetingPaused ? (
                      <PlayIcon className="h-4 w-4" />
                    ) : (
                      <PauseIcon className="h-4 w-4" />
                    )}
                  </button>
                ) : (
                  <button
                    type="button"
                    disabled={startDisabled}
                    onClick={() =>
                      isSessionInterrupted
                        ? handleResumeAfterInterruption()
                        : void handleStartMeeting()
                    }
                    className={[
                      'flex h-9 w-9 touch-manipulation items-center justify-center rounded-xl bg-emerald-50 text-emerald-600 transition-colors hover:bg-emerald-100 disabled:opacity-40 dark:bg-emerald-500/15 dark:text-emerald-300 dark:hover:bg-emerald-500/25',
                      startDisabled ? '' : 'motion-safe:animate-nudge-bounce',
                    ].join(' ')}
                    data-testid="meeting-start-button"
                    aria-describedby={startDisabled ? undefined : 'start-meeting-hint'}
                    title={t.startMeetingTitle}
                  >
                    <PlayIcon className="h-4 w-4" />
                  </button>
                )}
              </div>

              {/* Transport on one side, the things the meeting can be told to
                  do on the other. */}
              <span
                aria-hidden="true"
                data-testid="meeting-control-divider"
                className={MEETING_CONTROL_DIVIDER}
              />

              {copilotAssistEnabled ? (
                <CopilotFooterButton
                  active={isCopilotOpen}
                  unreadCount={copilotUi.unreadCount}
                  nudge={nudgeCopilot}
                  hint={showCopilotHint}
                  onClick={handleToggleCopilot}
                />
              ) : null}

              {TRANSLATION_HARD_ENABLED && !nativeMode && (
                <button
                  ref={translationHardButtonRef}
                  type="button"
                  onClick={handleTranslationHard}
                  disabled={!canRequestTranslationHard}
                  aria-keyshortcuts="Space"
                  aria-label={t.translationHard}
                  title={`${t.translationHard} (${t.translationHardHint})`}
                  data-testid="translation-hard-button"
                  className={`${MEETING_CONTROL_PILL} bg-slate-100 text-slate-600 hover:bg-slate-200 disabled:cursor-not-allowed disabled:opacity-40 dark:bg-white/5 dark:text-[var(--text-primary)] dark:hover:bg-white/10`}
                >
                  <TranslateIcon className="h-4 w-4 shrink-0" />
                  <span className={MEETING_CONTROL_PILL_LABEL}>{t.translationHard}</span>
                </button>
              )}

              <div ref={contextButtonRef} className="relative flex flex-col items-center gap-1">
                <button
                  type="button"
                  onClick={handleToggleContextModal}
                  disabled={!isContextSessionReady}
                  aria-expanded={isContextPanelOpen}
                  aria-label={t.realtimeContextLabel}
                  title={t.realtimeContextLabel}
                  className={[
                    MEETING_CONTROL_PILL,
                    'disabled:cursor-not-allowed disabled:opacity-40',
                    isContextPanelOpen
                      ? 'bg-emerald-100 text-emerald-700'
                      : 'bg-slate-100 text-slate-600 dark:bg-white/5 dark:text-[var(--text-primary)] dark:hover:bg-white/10',
                  ].join(' ')}
                >
                  <NoteIcon className="h-4 w-4 shrink-0" />
                  <span className={MEETING_CONTROL_PILL_LABEL}>{t.realtimeContextLabel}</span>
                </button>

                {!isContextPanelOpen && justUpdatedContext && contextSavedCharCount !== null && (
                  <ContextSavedToast anchorRef={contextButtonRef}>
                    {fmt(t.realtimeContextSaved, { count: contextSavedCharCount })}
                  </ContextSavedToast>
                )}
              </div>

              {/* End Meeting kept apart from the rest: it is the one control
                  in the row that cannot be undone by pressing it again. */}
              <span
                aria-hidden="true"
                data-testid="meeting-control-divider"
                className={MEETING_CONTROL_DIVIDER}
              />

              <button
                type="button"
                onClick={() => {
                  if (!isOnline) {
                    setShowOfflineEndPrompt(true)
                    return
                  }
                  void handleEndMeeting()
                }}
                disabled={!canEndMeeting || !isOnline}
                aria-disabled={!canEndMeeting || !isOnline}
                aria-label={t.endMeeting}
                className={`${MEETING_CONTROL_PILL} bg-red-600 text-white hover:bg-red-700 disabled:opacity-40`}
                title={!isOnline ? t.offlineEndDisabledTooltip : t.endMeeting}
              >
                <StopIcon className="h-4 w-4 shrink-0" />
                <span className={MEETING_CONTROL_PILL_LABEL}>{t.endMeeting}</span>
              </button>
            </div>
          </div>
        </CollapsibleChrome>
      </div>

      <SharedMeetingDetectionModal
        open={sharedRoomUi.detectionModalVisible}
        context={detectionModalContext}
        offerState={sharedRoom.state}
        t={t}
        locale={locale}
        ownMeetingTitle={currentMeeting.title}
        ownUserName={user?.displayName ?? undefined}
        meetingCreatedAt={currentMeeting.createdAt}
        meetingSourceLang={currentMeeting.sourceLang}
        meetingTargetLang={currentMeeting.targetLang}
        busy={leavingSharedRoom}
        onYes={handleDetectionYes}
        onNo={handleDetectionNo}
        onCancel={dismissSharedRoomDetection}
        onClose={dismissSharedRoomDetection}
      />

      <SharedRoomLeaveConfirmModal
        open={sharedRoomUi.leaveConfirmOpen}
        meetingTitle={sharedRoomPresentation?.meetingTitle ?? null}
        members={sharedRoomPresentation?.participants ?? []}
        t={t}
        leaving={leavingSharedRoom}
        onCancel={sharedRoomUi.closeLeaveConfirm}
        onConfirmLeave={() => void handleConfirmLeaveSharedRoom()}
      />

      <InviteMembersModal
        meetingId={currentMeeting.id}
        meetingTitle={headerTitle}
        open={inviteMembersOpen}
        onClose={() => setInviteMembersOpen(false)}
        inRoomMemberNames={liveInviteActiveMembers.map((member) => member.name)}
        onInvited={(count) => {
          void reloadLiveInviteRoster()
          showSharedRoomToast(fmt(t.liveInviteSent, { count }))
        }}
      />

      {sharedRoomUi.toast ? (
        <MeetingTransientToast toast={sharedRoomUi.toast} onDismiss={sharedRoomUi.dismissToast} />
      ) : null}

      {copilotAssistEnabled && !mobileFooterOpen ? (
        <CopilotFloatingFab
          active={isCopilotOpen}
          unreadCount={copilotUi.unreadCount}
          nudge={nudgeCopilot}
          hint={showCopilotHint}
          onClick={handleToggleCopilot}
        />
      ) : null}

      {showOfflineEndPrompt ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="offline-end-prompt-title"
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 px-4"
        >
          <div className="w-full max-w-md rounded-2xl bg-white p-4 shadow-2xl dark:bg-[var(--bg-secondary)] sm:p-6">
            <p
              id="offline-end-prompt-title"
              className="text-base font-bold text-slate-950 dark:text-[var(--text-primary)] sm:text-lg"
            >
              {t.offlineCannotEndTitle}
            </p>
            <p className="mt-2 text-xs leading-relaxed text-slate-600 dark:text-[var(--text-secondary)] sm:text-sm">
              {t.offlineCannotEndBody}
            </p>
            <div className="mt-5 flex justify-end">
              <button
                type="button"
                onClick={() => setShowOfflineEndPrompt(false)}
                className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-blue-700 dark:bg-[var(--accent)] dark:hover:bg-[var(--accent-hover)]"
              >
                {t.close}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {durationWarningStep !== 'closed' ? (
        <MeetingDurationCheckInModal
          step={durationWarningStep}
          t={t}
          onYesContinue={handleDurationWarningYesContinue}
          onNoFromCheckIn={handleDurationWarningNoFromCheckIn}
          onNoGoBack={handleDurationWarningNoGoBack}
          onYesStop={handleDurationWarningYesStop}
        />
      ) : null}

      {isContextPanelOpen ? (
        <RealtimeContextControl
          value={contextInputValue}
          savedValue={savedContextValue}
          onValueChange={setContextInputValue}
          onSubmit={recording.sendContextUpdate}
          onClose={handleCloseContextModal}
          disabled={!isContextSessionReady}
          status={recording.contextUpdateStatus}
          errorMessage={recording.contextUpdateError}
        />
      ) : null}

      {feedbackOpen && currentMeeting ? (
        <MeetingFeedbackModal
          meeting={{
            title: endingMeetingTitleRef.current ?? currentMeeting.title,
            sourceLang: currentMeeting.sourceLang,
            targetLang: currentMeeting.targetLang,
            durationMinutes: Math.max(
              1,
              Math.round((completedDurationSeconds ?? elapsedSeconds) / 60)
            ),
          }}
          isSubmitting={isSavingMeeting}
          error={saveError}
          onSubmit={saveCompletedMeeting}
          onClose={handleCancelFeedback}
        />
      ) : null}
    </div>
  )
}

/** Renders the play glyph used by meeting transport controls. */
function PlayIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
      <path d="M8 5.14v13.72a1 1 0 0 0 1.5.86l11.04-6.86a1 1 0 0 0 0-1.72L9.5 4.28A1 1 0 0 0 8 5.14Z" />
    </svg>
  )
}

/** Renders the pause glyph used by meeting transport controls. */
function PauseIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 7v10M15 7v10" />
    </svg>
  )
}

/** Renders the note/context glyph used to open the realtime-context modal. */
function NoteIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9 4h9a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V8l4-4Z"
      />
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 4v4H5" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 13h6M9 16.5h6" />
    </svg>
  )
}

/** Renders the info glyph used by the realtime-context modal's scope banner. */
function InfoIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="9" />
      <path strokeLinecap="round" d="M12 11v5" />
      <path strokeLinecap="round" d="M12 8h.01" />
    </svg>
  )
}

/** Renders the save/disk glyph used by the realtime-context modal's Save button. */
function SaveIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M5 4h11l3 3v13a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1Z"
      />
      <path strokeLinecap="round" strokeLinejoin="round" d="M8 4v5h8V4" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M8 20v-6h8v6" />
    </svg>
  )
}
